import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAppContext } from "../../context/AppContext";
import {
  Pencil, Plus, Link, Trash2, Music, PlayCircle, Clock,
  Timer, CheckCircle, Eye, RotateCcw, Image, Volume2, VolumeX, Download, Upload, Columns3, List, Bell, BellOff
} from "lucide-react";
import { Col, Input, Row, message, Select,Modal  } from "antd";
import { Container } from "react-bootstrap";
import { useNavigate } from "react-router";
import apiPath from "../../constants/apiPath";
import "../../assets/styles/dashboard.css";
// Import static images
import buildImage from "../../assets/images/build.jpeg";
import grindImage from "../../assets/images/grind.jpeg";
import fuseImage from "../../assets/images/fuse.jpeg";
import { downloadWeekToExcel, parseExcelToWeekData } from "./excelUtils";
// --- Flicker fix (2026-09-11) ---------------------------------------------
// SessionHeader/WorkoutBlock/MediaCard are defined INSIDE Dashboard, so their
// function identity changes on every Dashboard re-render, which made React
// unmount+remount the whole header/blocks/media on any colour/title/media
// change ('flicker after every change'). These fixed module-scope wrappers keep
// a stable component identity and just delegate to the current inner impl, so
// React updates in place instead of remounting. Distinct types avoid any
// sibling reconciliation collision.
const StableHeader = ({ __impl }) => __impl();
const StableBlock = ({ __impl, ...rest }) => __impl(rest);
const StableMedia = ({ __impl, ...rest }) => __impl(rest);

const Dashboard = () => {
  const [activeDay, setActiveDay] = useState(() => {
    const now = new Date(new Date().toLocaleString("en-US", { timeZone: window._fhTimezone || "Australia/Sydney" }));
    return now.toLocaleDateString("en-CA", { timeZone: window._fhTimezone || "Australia/Sydney" });
  });
  // Shared plan state — lives in AppContext so Programme changes are instantly visible here
  const { weeklyPlan: fullWeekData, setWeeklyPlan: setFullWeekData } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [uploading, setUploading] = useState({});
  const [isTableMode, setIsTableMode] = useState(false); // Preview state
  const [socket, setSocket] = useState(null);
  const [showAddBlockModal, setShowAddBlockModal] = useState(false);
  const [newBlockName, setNewBlockName] = useState("");
  const [newBlockTitle, setNewBlockTitle] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState("");
  const [currentMode, setCurrentMode] = useState("hero");
  const [showSettings, setShowSettings] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [completionMessage, setCompletionMessage] = useState("You've just completed!");
  const [isWorkoutActive, setIsWorkoutActive] = useState(false);
  const [isWorkoutPaused, setIsWorkoutPaused] = useState(false);
  const [restartFlash, setRestartFlash] = useState(false);
  // Tracks natural workout completion so music can play through to end
  const workoutJustCompletedRef = useRef(false);
  const [chimeType, setChimeType] = useState(() => {
    try { return localStorage.getItem("fh_chime_type") || "classic"; } catch { return "classic"; }
  });
  const [coachPin, setCoachPin] = useState(() => {
    try {
      const stored = localStorage.getItem("fh_coach_pin");
      if (stored) return stored;
      const pin = String(Math.floor(100000 + Math.random() * 900000));
      localStorage.setItem("fh_coach_pin", pin);
      return pin;
    } catch { return "000000"; }
  });
  const [networkGroup, setNetworkGroup] = useState(null);
  const [networkPushing, setNetworkPushing] = useState(false);
  const playBell = useCallback((type = "transition") => {
    if (chimeType === "off") return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      if (chimeType === "whistle") {
        osc.type = "sine";
        if (type === "complete") {
          osc.frequency.setValueAtTime(1800, ctx.currentTime);
          osc.frequency.linearRampToValueAtTime(2200, ctx.currentTime + 0.15);
          osc.frequency.linearRampToValueAtTime(1600, ctx.currentTime + 0.4);
          gain.gain.setValueAtTime(0.35, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
          osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.6);
        } else {
          osc.frequency.setValueAtTime(1600, ctx.currentTime);
          osc.frequency.linearRampToValueAtTime(2000, ctx.currentTime + 0.2);
          gain.gain.setValueAtTime(0.3, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
          osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.35);
        }
      } else if (chimeType === "bell") {
        osc.type = "triangle";
        if (type === "complete") {
          osc.frequency.setValueAtTime(523, ctx.currentTime);
          osc.frequency.setValueAtTime(659, ctx.currentTime + 0.2);
          gain.gain.setValueAtTime(0.5, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.2);
          osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 1.2);
        } else {
          osc.frequency.setValueAtTime(880, ctx.currentTime);
          gain.gain.setValueAtTime(0.45, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.9);
          osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.9);
        }
      } else {
        // Classic
        osc.type = "sine";
        if (type === "complete") {
          osc.frequency.setValueAtTime(880, ctx.currentTime);
          osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.18);
          gain.gain.setValueAtTime(0.4, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
          osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.7);
        } else {
          osc.frequency.setValueAtTime(1047, ctx.currentTime);
          gain.gain.setValueAtTime(0.3, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
          osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.4);
        }
      }
      ctx.close();
    } catch {}
  }, [chimeType]);
  const [textColor, setTextColor] = useState("#FCDC08");
const [borderColor, setBorderColor] = useState("#FCDC08");
const [tempTextColor, setTempTextColor] = useState("#FCDC08");
const [tempBorderColor, setTempBorderColor] = useState("#FCDC08");
    const [selectedColor, setSelectedColor] = useState("#000000ff");
const [fontA, setFontA] = useState("Horizon");
const [fontAColor, setFontAColor] = useState("#FCDC08");
const [fontB, setFontB] = useState("Horizon");
const [fontBColor, setFontBColor] = useState("#F7F7F7");
const [topCompletionMessage, setTopCompletionMessage] = useState("");
const [transitionMessage, setTransitionMessage] = useState("");
const [backgroundColor, setBackgroundColor] = useState("#000000");

const [tempColor, setTempColor] = useState(selectedColor);
useEffect(() => {
  if (resolveDayData(fullWeekData?.full_week, activeDay)?.color) {
    setSelectedColor(resolveDayData(fullWeekData?.full_week, activeDay).color);
    setTempColor(resolveDayData(fullWeekData?.full_week, activeDay).color);
  }
}, [activeDay, fullWeekData]);
const handleColorChange = (color) => {
  setSelectedColor(color);
  setTempColor(color);
  
  // Persist to backend / local state through autoSave
  autoSave({ new_color: color });
  
  // Update local state immediately
  setFullWeekData(prev => {
    if (!prev) return prev;
    const newData = JSON.parse(JSON.stringify(prev));
    const wd = getWritableDayData(newData.full_week, activeDay);
    if (wd) wd.color = color;
    return newData;
  });
  
  // Notify live clients via WebSocket
  if (socket && socket.readyState === WebSocket.OPEN) {
    try {
      socket.send(JSON.stringify({ type: "update_color", day: activeDay, color }));
      // Theme color applied
    } catch (err) {
      message.error("Failed to send theme color");
    }
  } else {
    // Theme color updated
  }
};

const handleTextColorChange = (color) => {
  setTempTextColor(color);
  setTextColor(color);
  
  // Persist to backend / local state through autoSave
  autoSave({ new_text_color: color });
  
  // Notify live clients via WebSocket
  if (socket && socket.readyState === WebSocket.OPEN) {
    try {
      socket.send(JSON.stringify({ 
        type: "update_text_color", 
        day: activeDay, 
        text_color: color 
      }));
      // Accent color applied
    } catch (err) {
      message.error("Failed to send text color");
    }
  } else {
    // Accent color updated
  }
};

const handleBorderColorChange = (color) => {
  setTempBorderColor(color);
  setBorderColor(color);
  
  // Persist to backend / local state through autoSave
  autoSave({ new_border_color: color });
  
  // Notify live clients via WebSocket
  if (socket && socket.readyState === WebSocket.OPEN) {
    try {
      socket.send(JSON.stringify({ 
        type: "update_border_color", 
        day: activeDay, 
        border_color: color 
      }));
      // Border color applied
    } catch (err) {
      message.error("Failed to send border color");
    }
  } else {
    // Border color updated
  }
};
  const handleDownloadExcel = () => {
    if (!fullWeekData) {
      message.warning("No workout data available to download");
      return;
    }
    
    const success = downloadWeekToExcel(fullWeekData);
    if (success) {
      message.success("Workout plan downloaded as Excel!");
    }
  };

  const handleExcelFileSelect = (event) => {
  const file = event.target.files[0];
  if (!file) {
    return;
  }
  
  
  // Check if it's an Excel file
  const validExtensions = ['.xlsx', '.xls', '.xlsm'];
  const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  
  
  if (!validExtensions.includes(fileExtension)) {
    message.error("Please select an Excel file (.xlsx, .xls, .xlsm)");
    return;
  }
  
  if (file.size > 10 * 1024 * 1024) {
    message.error("File size should be less than 10MB");
    return;
  }
  
  setExcelFile(file);
  setIsUploadModalOpen(true);
  
  // Clear the input so same file can be selected again
  event.target.value = null;
};

  const handleUploadExcel = async () => {
  
  if (!excelFile) {
    message.error("Please select a file first");
    return;
  }

  setIsProcessingExcel(true);
  setUploadProgress(10);

  try {
    setUploadProgress(30);
    
    const parsedData = await parseExcelToWeekData(excelFile);
    
    // Debug: Check Friday's B_BLOCK specifically
    if (parsedData.days?.FRIDAY?.data?.B_BLOCK) {
    }
    
    setUploadProgress(60);
    
    const token = localStorage.getItem("accessToken");
    const response = await fetch(`${apiPath.baseURL}${apiPath.updatePlanFromExcel}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ weekly_plan: parsedData })
    });
    
    const result = await response.json();
    
    if (response.ok && result.status === 200) {
      message.success(result.message || "Workout plan updated successfully!");
      await fetchWeeklyPlan(true);
      setIsUploadModalOpen(false);
      setExcelFile(null);
    } else {
      message.error(result.message || "Failed to update plan");
    }
    
  } catch (error) {
    message.error(error.message || "Error processing Excel file");
  } finally {
    setIsProcessingExcel(false);
    setUploadProgress(100);
  }
};

  // Add Excel upload modal
  const ExcelUploadModal = () => {
  if (!isUploadModalOpen) {
    return null;
  }
  const handleModalCancel = () => {
    setIsUploadModalOpen(false);
    setExcelFile(null);
    setUploadProgress(0);
  };

  const handleModalUpload = () => {
    if (excelFile) {
      handleUploadExcel();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10001
    }}>
      <div style={{
        background: '#1a1a2e',
        padding: '30px',
        borderRadius: '12px',
        border: '2px solid #004aad',
        minWidth: '500px',
        maxWidth: '90vw'
      }}>
        <h2 style={{ color: '#fcdc08', marginBottom: '20px' }}>
          Upload Excel Workout Plan
        </h2>
        
        <div style={{ marginBottom: '20px' }}>
          <div style={{ color: 'white', marginBottom: '10px' }}>
            Selected File: <strong>{excelFile ? excelFile.name : 'No file selected'}</strong>
          </div>
          
          {/* Upload Progress */}
          {isProcessingExcel && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginBottom: '5px'
              }}>
                <span style={{ color: '#ccc' }}>Processing...</span>
                <span style={{ color: '#fcdc08' }}>{uploadProgress}%</span>
              </div>
              <div style={{
                width: "100%",
                height: "10px",
                background: "#333",
                borderRadius: "5px",
                overflow: "hidden"
              }}>
                <div style={{
                  width: `${uploadProgress}%`,
                  height: "100%",
                  background: "#004aad",
                  transition: "width 0.3s ease"
                }}></div>
              </div>
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleModalCancel}
            disabled={isProcessingExcel}
            style={{
              padding: "10px 20px",
              background: "#666",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: isProcessingExcel ? "not-allowed" : "pointer",
              opacity: isProcessingExcel ? 0.6 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleModalUpload}
            disabled={!excelFile || isProcessingExcel}
            style={{
              padding: "10px 20px",
              background: excelFile && !isProcessingExcel ? "#004aad" : "#555",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: excelFile && !isProcessingExcel ? "pointer" : "not-allowed",
              opacity: excelFile && !isProcessingExcel ? 1 : 0.6
            }}
          >
            {isProcessingExcel ? 'Processing...' : 'Upload'}
          </button>
        </div>
        
        {/* Debug Info (visible in dev only) */}
        <div style={{ 
          marginTop: '20px', 
          padding: '10px', 
          background: '#2a2a3e',
          borderRadius: '5px',
          fontSize: '12px',
          color: '#999',
          display: process.env.NODE_ENV === 'development' ? 'block' : 'none'
        }}>
          <div>Modal State: OPEN</div>
          <div>File Selected: {excelFile ? 'YES' : 'NO'}</div>
          <div>Processing: {isProcessingExcel ? 'YES' : 'NO'}</div>
          <div>Progress: {uploadProgress}%</div>
        </div>
      </div>
    </div>
  );
};
  // Audio states
  const [audioPlaying, setAudioPlaying] = useState({
    pre_hype: false,
    warmup: false,
    workout: false,
    cooldown: false
  });
  const [audioVolume, setAudioVolume] = useState({
    pre_hype: 0.7,
    warmup: 0.7,
    workout: 0.7,
    cooldown: 0.7
  });

  const audioRefs = {
    pre_hype: useRef(null),
    warmup: useRef(null),
    workout: useRef(null),
    cooldown: useRef(null)
  };
  const navigate = useNavigate();
  const stopOtherAudio = (keepType) => {
  Object.keys(audioRefs).forEach(type => {
    if (type !== keepType) {
      const audio = audioRefs[type].current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
        setAudioPlaying(prev => ({ ...prev, [type]: false }));
      }
    }
  });
};
 
  // Static available images - no need to fetch from backend
  const availableImages = [
    { value: "build", label: "Build", path: buildImage },
    { value: "grind", label: "Grind", path: grindImage },
    { value: "fuse", label: "Fuse", path: fuseImage }
  ];
  const fileInputs = {
    pre_hype_video: useRef(null),
    warmup_video: useRef(null),
    pre_hype_audio: useRef(null),
    warmup_audio: useRef(null),
    cooldown_audio: useRef(null),
    workout_audio: useRef(null),
    background_image: useRef(null),
  };
  // Generate current week's dates (Mon-Sun) in YYYY-MM-DD format
  const getWeekDates = () => {
    const now = new Date(new Date().toLocaleString("en-US", { timeZone: window._fhTimezone || "Australia/Sydney" }));
    const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon...
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(now);
    monday.setDate(now.getDate() + mondayOffset);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      dates.push(d.toLocaleDateString("en-CA", { timeZone: window._fhTimezone || "Australia/Sydney" }));
    }
    return dates;
  };
  const DAYS = getWeekDates();
  const DAY_LABELS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Helper: resolve day data from full_week — checks dates sub-object first, then top-level
  const resolveDayData = (fullWeek, dayKey) => {
    if (!fullWeek) return undefined;
    const dateData = fullWeek.dates?.[dayKey];
    const topData = fullWeek[dayKey];
    // If it's a date key, also check the day name for legacy data
    let dayNameData;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) {
      const dayName = new Date(dayKey + 'T12:00:00').toLocaleString('en-US', { weekday: 'long', timeZone: 'Australia/Sydney' }).toUpperCase();
      dayNameData = fullWeek[dayName];
    }
    // Merge: date-keyed data takes priority, but fill in missing blocks from day-name data
    if (dateData && dayNameData) {
      const merged = { ...dayNameData, ...dateData };
      if (dayNameData.data || dateData.data) {
        merged.data = { ...(dayNameData.data || {}), ...(dateData.data || {}) };
      }
      return merged;
    }
    return dateData || topData || dayNameData || undefined;
  };

  // Helper: get writable reference — ALWAYS writes to dates sub-object
  // Copies merged day-name + date data on first write for consistency
  const getWritableDayData = (fullWeek, dayKey) => {
    if (!fullWeek) return undefined;
    if (!fullWeek.dates) fullWeek.dates = {};
    // If date entry already exists, use it
    if (fullWeek.dates[dayKey]) return fullWeek.dates[dayKey];
    // First write: merge existing data from day name + any date data
    const merged = JSON.parse(JSON.stringify(resolveDayData(fullWeek, dayKey) || {}));
    fullWeek.dates[dayKey] = merged;
    return fullWeek.dates[dayKey];
  };
  // NEW: Global save timers to track all pending saves
  const globalSaveTimers = useRef({});
  const pendingSavesRef = useRef({});
  const blockDataCacheRef = useRef({});
  const fetchWeeklyPlanRef = useRef(null); // latest fetchWeeklyPlan for the once-mounted WS handler
  // Refs so WS handler always reads latest values without needing to reconnect
  const activeDayRef = useRef(activeDay);
  const fullWeekDataRef = useRef(fullWeekData);
  useEffect(() => { activeDayRef.current = activeDay; }, [activeDay]);
  useEffect(() => { fullWeekDataRef.current = fullWeekData; }, [fullWeekData]);
  // WebSocket command sender (shared across components)
  const sendWsCommand = useCallback((type, payload = {}) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    try {
      socket.send(JSON.stringify({ type, ...payload }));
      return true;
    } catch (err) { return false; }
  }, [socket]);
  // NEW: Debounced autoSave function
  const autoSave = useCallback((updates) => {
    const token = localStorage.getItem("accessToken");

    // For block/exercise saves, skip the full state update to avoid
    // re-rendering the component and losing form input focus/state.
    // Block data is already managed locally by ExerciseBlockEditor.
    const isBlockSave = !!(updates.block && updates.block_data);

    if (!isBlockSave) {
      // Update local state immediately for non-block changes (colors, titles, etc.)
      setFullWeekData(prev => {
        if (!prev) return prev;
        const newData = JSON.parse(JSON.stringify(prev));
        const dayData = getWritableDayData(newData.full_week, activeDay);
        if (updates.new_completion_message !== undefined) {
          if (!dayData.completion_settings) {
            dayData.completion_settings = {};
          }
          dayData.completion_settings.message = updates.new_completion_message;
          setCompletionMessage(updates.new_completion_message);
        }
        if (updates.new_title !== undefined) dayData.name = updates.new_title;
        if (updates.new_image !== undefined) {
          dayData.image = updates.new_image;
        }
        if (updates.new_color !== undefined) {
          dayData.color = updates.new_color;
        }
        if (updates.new_text_color !== undefined) {
          dayData.text_color = updates.new_text_color;
          setTextColor(updates.new_text_color);
        }
        if (updates.new_border_color !== undefined) {
          dayData.border_color = updates.new_border_color;
          setBorderColor(updates.new_border_color);
        }
        if (updates.new_font_a !== undefined) {
          dayData.font_a = updates.new_font_a;
        }
        if (updates.new_font_a_color !== undefined) {
          dayData.font_a_color = updates.new_font_a_color;
        }
        if (updates.new_font_b !== undefined) {
          dayData.font_b = updates.new_font_b;
        }
        if (updates.new_font_b_color !== undefined) {
          dayData.font_b_color = updates.new_font_b_color;
        }
        if (updates.new_top_completion_message !== undefined) {
          dayData.top_completion_message = updates.new_top_completion_message;
        }
        if (updates.new_transition_message !== undefined) {
          dayData.transition_message = updates.new_transition_message;
        }
        if (updates.new_background_color !== undefined) {
          dayData.background_color = updates.new_background_color;
        }
        return newData;
      });

      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
    } else {
      // For block saves, update the ref-based cache so data persists
      // if the component re-mounts, without triggering a state re-render
      if (blockDataCacheRef.current && updates.block && updates.block_data) {
        blockDataCacheRef.current[`${activeDay}_${updates.block}`] = updates.block_data;
      }
    }
   
    // NEW: Create a unique key for this save (block-specific or general)
    const saveKey = updates.block || 'general';
   
    // Store the pending save data
    pendingSavesRef.current[saveKey] = updates;
   
    // Clear any existing timer for this key
    if (globalSaveTimers.current[saveKey]) {
      clearTimeout(globalSaveTimers.current[saveKey]);
    }
   
    // Set new timer (500ms delay — near real-time saves)
    globalSaveTimers.current[saveKey] = setTimeout(async () => {
      try {
        const payload = { day: activeDay, ...pendingSavesRef.current[saveKey] };
       
        // Clear the pending save
        delete pendingSavesRef.current[saveKey];
       
        const response = await fetch(`${apiPath.baseURL}gym_owner/update_workout_plan`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload)
        });
       
        const result = await response.json();
        if (result.status !== 200) {
          if (result.error === "Nothing to update") {
          } else {
            message.error(result.error || "");
          }
        } else {
          const selectedImage = availableImages.find(img => img.value === updates.new_image);
          if (result.updated_plan?.text_color) {
          setTempTextColor(result.updated_plan.text_color);
        }
        if (result.updated_plan?.border_color) {
          setTempBorderColor(result.updated_plan.border_color);
        }
        }
      } catch (err) {
        message.error("");
      }
    }, 500);
   
  }, [activeDay]);
  // NEW: Clean up all timers on component unmount
  useEffect(() => {
    return () => {
      // Clear all timers
      Object.values(globalSaveTimers.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);
  // WebSocket: Control Stream in real-time
  useEffect(() => {
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
  
    const connect = () => {
    try {
      const token = localStorage.getItem("accessToken");
      // const userId = localStorage.getItem("userId"); // Get user ID
      const baseUrl = apiPath.baseURL.replace('/api/v1/', '/').replace('http://', 'ws://').replace('https://', 'wss://');
      // const ws = new WebSocket(`${baseUrl}ws/preview/`);
      const wsUrl = `${baseUrl}ws/preview/?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      ws.onopen = () => {
        setSocket(ws);
        reconnectAttempts = 0;
      };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          // Multi-dashboard live sync (restored 2026-09-16): when another
          // computer/session saves, silently refetch so every open Dashboard
          // and the screens stay in sync. Skip if we originated the save;
          // defer while mid-edit. Backend broadcasts plan_updated on every save.
          if (data.type === 'plan_updated') {
            try {
              // (No source-user skip: studios run the same owner login on multiple
              //  computers, so skipping by user id would block cross-computer sync.
              //  A refetch of our own save is harmless — silent, deferred while editing.)
              if (window.__fhPlanSyncTimer) clearTimeout(window.__fhPlanSyncTimer);
              window.__fhPlanSyncTimer = setTimeout(() => {
                const ae = document.activeElement;
                const isEditing = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
                if (isEditing) {
                  window.__fhPlanSyncTimer = setTimeout(() => { if (fetchWeeklyPlanRef.current) fetchWeeklyPlanRef.current(true); }, 5000);
                  return;
                }
                if (fetchWeeklyPlanRef.current) fetchWeeklyPlanRef.current(true);
              }, 400);
            } catch (_) {}
            return;
          }
          const currentActiveDay = activeDayRef.current;
          const currentFullWeekData = fullWeekDataRef.current;
          if (data.day === currentActiveDay) {
            setIsTableMode(data.type === "show_table");

            // Get current media from state
            const currentMedia = resolveDayData(currentFullWeekData?.full_week, currentActiveDay)?.media || {};
            
            // Handle workout audio based on workout state
            if (data.type === 'timer_update' || data.type === 'show_countdown') {
              // Workout started or countdown started - play workout audio if available
              if (currentMedia.workout_audio && !audioPlaying.workout) {
                if (audioPlaying.warmup) pauseAudio('warmup');
                if (audioPlaying.pre_hype) pauseAudio('pre_hype');
                playAudio('workout');
              }
            } else if (data.type === 'workout_complete' || data.type === 'workout_stopped') {
              // Workout completed or stopped - stop workout audio
              // pauseAudio('workout');
            } else if (data.type === 'pause_workout' || data.type === 'workout_paused') {
              // Workout paused - pause workout audio
              pauseAudio('workout');
            } else if (data.type === 'resume_workout' || data.type === 'workout_resumed') {
              // Workout resumed - resume workout audio if available
              if (currentMedia.workout_audio && !audioPlaying.workout) {
                playAudio('workout');
              }
            }
            // Handle mode changes
            else if (data.type === 'show_pre_hype') {
              // When receiving show_pre_hype from backend, ensure pre-hype audio plays
              stopOtherAudio('pre_hype');
              setCurrentMode("pre_hype");
              if (currentMedia.pre_hype_audio && !audioPlaying.pre_hype) {
                playAudio('pre_hype');
              }
            }
            else if (data.type === 'show_warmup') {
              // When receiving show_warmup from backend, ensure warmup audio plays
              stopOtherAudio('warmup');
              setCurrentMode("warmup");
              if (currentMedia.warmup_audio && !audioPlaying.warmup) {
                playAudio('warmup');
              }
            }
            else if (data.type === 'show_hero') {
              // Hero mode — stop pre_hype and warmup but keep workout music playing
              setCurrentMode("hero");
              if (audioPlaying.pre_hype) pauseAudio('pre_hype');
              if (audioPlaying.warmup) pauseAudio('warmup');
            }
            else if (data.type === 'show_table') {
              // Table preview — audio keeps playing
              setCurrentMode("table");
            }
            // Handle audio control commands
            else if (data.type === 'stop_audio') {
              const audioType = data.payload?.audio_type;
              if (audioType) {
                pauseAudio(audioType);
              }
            }
            else if (data.type === 'play_audio') {
              const playAudioType = data.payload?.audio_type;
              if (playAudioType && currentMedia[`${playAudioType}_audio`]) {
                // Stop other audio before playing new one
                if (playAudioType === 'pre_hype' || playAudioType === 'warmup') {
                  stopOtherAudio(playAudioType);
                }
                playAudio(playAudioType);
              }
            }
            else if (data.type === 'stop_all_audio') {
              // ⚠️  DO NOT MODIFY WITHOUT DALEY'S EXPLICIT APPROVAL ⚠️
              // Approved behaviour (2026-04-09):
              //   stop_all_audio is broadcast by the backend any time a mode
              //   command (show_table, show_hero, show_cooldown, etc.) arrives.
              //   It must NEVER cut the workout track automatically.
              //   Workout audio stops ONLY via explicit user actions:
              //     • RESTART       → handleRestart calls stopAllAudio() directly
              //     • RESET         → handlePreviewToggle calls stopAllAudio() directly
              //     • COOL-DOWN     → handleCooldown calls pauseAudio('workout')
              //     • PRE-HYPE start → handlePreHype calls pauseAudio('workout')
              //     • WARM-UP start  → handleWarmup calls pauseAudio('workout')
              //   Everything else (PREVIEW, natural workout completion, show_hero
              //   relays) must leave the workout track untouched.
              pauseAudio('pre_hype');
              pauseAudio('warmup');
              pauseAudio('cooldown');
              // workout audio intentionally omitted — plays until track ends
            }
          }
        } catch (err) {
        }
      };
      ws.onclose = (e) => {
        setSocket(null);
      
        if (reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          setTimeout(connect, 2000 * reconnectAttempts);
        } else {
        }
      };
      ws.onerror = (error) => {
        // Don't show error message on every attempt
        if (reconnectAttempts === 0) {
          message.error("Live sync connection failed");
        }
      };
    } catch (err) {
    }
  };
  connect();
  return () => {
    if (socket) {
      socket.close();
      setSocket(null);
    }
  };
}, []); // Connect once — activeDay/fullWeekData read via refs in the handler
  // Fetch Weekly Plan
  const fetchWeeklyPlan = async (silent = false) => {
    // silent=true refreshes data WITHOUT flipping the loading screen, so the
    // Dashboard (and the <audio> elements) never unmount on a save. This stops
    // the on-save flicker and the workout-music cutout. Only the initial load
    // shows the spinner.
    if (!silent) setLoading(true);
    const token = localStorage.getItem("accessToken");
    if (!token) { setError("Login required"); if (!silent) setLoading(false); return; }
    try {
      const res = await fetch(`${apiPath.baseURL}${apiPath.getWeeklyPlan}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.status === 200) {
        if (data.timezone) {
          window._fhTimezone = data.timezone;
          // Recalculate today's date now that we have the correct timezone
          const correctToday = new Date().toLocaleDateString("en-CA", { timeZone: data.timezone });
          if (!silent) setActiveDay(correctToday);
        }
        setFullWeekData(JSON.parse(JSON.stringify(data)));
        const correctActiveDay = data.timezone
          ? new Date().toLocaleDateString("en-CA", { timeZone: data.timezone })
          : activeDay;
        const currentDayData = resolveDayData(data.full_week, correctActiveDay);
      // Only (re)load view settings on the INITIAL load. A silent live-sync refetch must
      // NOT yank the user back to today or reset their in-progress colour/day edits.
      if (!silent) {
      if (currentDayData?.completion_settings?.message) {
        setCompletionMessage(currentDayData.completion_settings.message);
      } else {
        setCompletionMessage("You've just completed!");
      }if (currentDayData?.text_color) {
        setTextColor(currentDayData.text_color);
        setTempTextColor(currentDayData.text_color);
      }
      if (currentDayData?.color) {
  setSelectedColor(currentDayData.color);
  setTempColor(currentDayData.color);
}
      
      // Set border color from data
      if (currentDayData?.border_color) {
        setBorderColor(currentDayData.border_color);
        setTempBorderColor(currentDayData.border_color);
      }
      setFontA(currentDayData?.font_a || "Horizon");
      setFontAColor(currentDayData?.font_a_color || "#FCDC08");
      setFontB(currentDayData?.font_b || "Horizon");
      setFontBColor(currentDayData?.font_b_color || "#F7F7F7");
      setTopCompletionMessage(currentDayData?.top_completion_message || "");
      setTransitionMessage(currentDayData?.transition_message || "");
      setBackgroundColor(currentDayData?.background_color || "#000000");
      }
      }else if (data.status === 403) {
      // Subscription invalid - show message and redirect to login
      message.error(data.message || "Subscription issue. Please log in again.");
      
      // Clear local storage
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userData");
      
      // Redirect to login page
      setTimeout(() => {
        navigate('/');
      }, 1500);
    }
  
    } catch (err) {
      setError("Failed to load plan");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  fetchWeeklyPlanRef.current = fetchWeeklyPlan; // keep the WS live-sync handler pointed at the current fn
  useEffect(() => {
  if (resolveDayData(fullWeekData?.full_week, activeDay)?.completion_settings?.message) {
    setCompletionMessage(resolveDayData(fullWeekData?.full_week, activeDay).completion_settings.message);
  } else {
    setCompletionMessage("You've just completed!");
  }
  const dayData = resolveDayData(fullWeekData?.full_week, activeDay);
  setFontA(dayData?.font_a || "Horizon");
  setFontAColor(dayData?.font_a_color || "#FCDC08");
  setFontB(dayData?.font_b || "Horizon");
  setFontBColor(dayData?.font_b_color || "#F7F7F7");
  setTopCompletionMessage(dayData?.top_completion_message || "");
  setTransitionMessage(dayData?.transition_message || "");
  setBackgroundColor(dayData?.background_color || "#000000");
}, [activeDay, fullWeekData]);
  useEffect(() => {
    // Set activeDay to today's date in Sydney timezone (YYYY-MM-DD)
    const now = new Date(new Date().toLocaleString("en-US", { timeZone: window._fhTimezone || "Australia/Sydney" }));
    const todayDate = now.toLocaleDateString("en-CA", { timeZone: window._fhTimezone || "Australia/Sydney" });
    setActiveDay(todayDate);
    // If Programme already loaded data into context, use it — don't overwrite with a stale API fetch
    if (fullWeekData) {
      setLoading(false);
    } else {
      fetchWeeklyPlan();
    }
    // Check if this account is a network master
    const tokenForNetwork = localStorage.getItem("accessToken");
    if (tokenForNetwork) {
      fetch(`${apiPath.baseURL}gym_owner/network/group`, {
        headers: { Authorization: `Bearer ${tokenForNetwork}` },
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.status === 200 && data.group) setNetworkGroup(data.group);
        })
        .catch(() => {});
    }

    // Sync coach PIN from backend (authoritative source)
    const token = localStorage.getItem("accessToken");
    if (token) {
      fetch(`${apiPath.baseURL}gym_owner/get_coach_pin`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.json())
        .then((data) => {
          const backendPin = data?.data?.coach_pin;
          if (backendPin) {
            setCoachPin(backendPin);
            localStorage.setItem("fh_coach_pin", backendPin);
          }
        })
        .catch(() => {});
    }
  }, []);
  const currentDay = resolveDayData(fullWeekData?.full_week, activeDay) || {};
  const media = currentDay.media || {};
  // ── Diagnostic logging (platform telemetry -> gym_owner/diag_log, 48h retention) ──
  // Non-fatal, batched, fire-and-forget. Used to catch audio dropouts / flicker in the wild.
  const diagBuf = useRef([]);
  const diagTimer = useRef(null);
  const flushDiag = () => {
    const events = diagBuf.current.splice(0, diagBuf.current.length);
    if (!events.length) return;
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    try {
      fetch(`${apiPath.baseURL}gym_owner/diag_log`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ source: "dashboard", events }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) {}
  };
  const diagLog = (event, detail) => {
    try {
      diagBuf.current.push({ event, detail: { ...(detail || {}), ts: new Date().toISOString() } });
      if (diagBuf.current.length >= 10) { flushDiag(); return; }
      if (diagTimer.current) clearTimeout(diagTimer.current);
      diagTimer.current = setTimeout(flushDiag, 4000);
    } catch (_) {}
  };
  // Auto-recover audio that stalls mid-playback (network hiccup on the CloudFront track).
  const handleAudioStall = (type, reason) => {
    diagLog("audio_stall", { type, reason });
    const audio = audioRefs[type]?.current;
    if (audio && audioPlaying[type]) {
      audio.play()
        .then(() => diagLog("audio_stall_recovered", { type }))
        .catch((e) => diagLog("audio_stall_recover_failed", { type, err: String(e) }));
    }
  };

  // Audio control functions
  const playAudio = (type) => {
    const audio = audioRefs[type].current;
    const currentMedia = resolveDayData(fullWeekData?.full_week, activeDay)?.media || {};
    if (audio && currentMedia[`${type}_audio`]) {
      audio.volume = audioVolume[type];
      audio.play().then(() => {
        setAudioPlaying(prev => ({ ...prev, [type]: true }));
        diagLog("audio_play", { type });
      }).catch(err => {
        diagLog("audio_play_failed", { type, err: String(err) });
        message.error(`Failed to play ${type} audio`);
      });
    }
  };
  const pauseAudio = (type) => {
    const audio = audioRefs[type].current;
    if (audio) {
      audio.pause();
      setAudioPlaying(prev => ({ ...prev, [type]: false }));
    }
  };
  const stopAllAudio = () => {
    Object.keys(audioRefs).forEach(type => {
      const audio = audioRefs[type].current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    setAudioPlaying({
      pre_hype: false,
      warmup: false,
      workout: false
    });
  };
  const handleVolumeChange = (type, volume) => {
    setAudioVolume(prev => ({ ...prev, [type]: volume }));
    const audio = audioRefs[type].current;
    if (audio) {
      audio.volume = volume;
    }
  };
  // Calculate and update total duration for header
  useEffect(() => {
    const totalDuration = calculateTotalDuration(currentDay);
    window.currentDayTotalDuration = totalDuration;
    localStorage.setItem('currentDayTotalDuration', totalDuration.toString());
    // Trigger a custom event to notify header
    window.dispatchEvent(new CustomEvent('totalDurationChanged', { detail: totalDuration }));
  }, [currentDay, activeDay]);
  // Duration Calculations - UPDATED FOR MINUTES AND SECONDS
  const calculateBlockDuration = (blockDuration) => {
    // Convert block duration string "MM:SS" to total seconds
    if (typeof blockDuration === 'string' && blockDuration.includes(':')) {
      const [minutes, seconds] = blockDuration.split(':').map(Number);
      return minutes * 60 + (seconds || 0);
    }
    // If it's just a number (for backward compatibility)
    return parseInt(blockDuration) * 60 || 0;
  };
  
  const calculateTotalDuration = (dayData) => {
    let totalSeconds = 0;
    Object.keys(dayData.data || {}).forEach(blockKey => {
      if (blockKey.endsWith("_BLOCK") && dayData.data[blockKey]) {
        totalSeconds += calculateBlockDuration(dayData.data[blockKey].duration || "0:00");
      }
    });
    return totalSeconds;
  };
  
  // Helper function to format seconds to MM:SS
  const formatDuration = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Helper function to parse duration string to total seconds
  const parseDuration = (durationStr) => {
    if (!durationStr) return 0;
    if (typeof durationStr === 'string') {
      const [minutes, seconds] = durationStr.split(':').map(Number);
      return minutes * 60 + (seconds || 0);
    }
    return durationStr * 60; // For backward compatibility
  };
  // Upload Media
  const uploadMedia = async (file, category) => {
  if (!file) return;
  if (category.includes('_audio')) {
    const validAudioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.webm'];
    const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validAudioExtensions.includes(fileExtension)) {
      message.error(`Please upload audio in a supported format: ${validAudioExtensions.join(', ')}`);
      return;
    }
  }

  const token = localStorage.getItem("accessToken");
  const formData = new FormData();
  formData.append("file", file);
  formData.append("category", category);
  formData.append("day", activeDay);
  setUploading(prev => ({ ...prev, [category]: true }));
  
  try {
    const res = await fetch(`${apiPath.baseURL}${apiPath.uploadMedia}`, {
      method: "POST",
      headers: { 
        Authorization: `Bearer ${token}`,
        // Note: Don't set Content-Type for FormData, let browser set it
      },
      body: formData
    });
  
    const data = await res.json();
  
    if (data.status === 200) {
      const successMessage = data.data?.supports_hls 
        ? `${category.replace(/_/g, ' ')} uploaded and processed for streaming!` 
        : `${category.replace(/_/g, ' ')} uploaded successfully!`;
      
      message.success(successMessage);
    
      // Update state with new media URL
      setFullWeekData(prev => {
        const newData = JSON.parse(JSON.stringify(prev));
        const wd = getWritableDayData(newData.full_week, activeDay);
        if (!wd.media) { wd.media = {}; }
        wd.media[category] = data.data.media_url;
        return newData;
      });
    
    } else {
      message.error(data.message || "Upload failed");
    }
  } catch (err) {
    message.error("Upload failed - network error");
  } finally {
    setUploading(prev => ({ ...prev, [category]: false }));
  }
};
  const handleFileSelect = (e, category) => {
    const file = e.target.files[0];
    if (file) uploadMedia(file, category);
    e.target.value = null;
  };
  // Handle image selection change
  const handleImageChange = (imageName) => {
    autoSave({ new_image: imageName });
  };
  // Get the current image path for preview
  const getCurrentImagePath = () => {
    if (!currentDay.image) return null;
    const selectedImage = availableImages.find(img => img.value === currentDay.image);
    return selectedImage ? selectedImage.path : null;
  };
  // Live Control: Toggle Stream
  const toggleStream = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      message.error("Live sync not ready");
      return;
    }
    const type = isTableMode ? "show_hero" : "show_table";
    socket.send(JSON.stringify({ type, day: activeDay }));
    setIsTableMode(!isTableMode);
    message.success(isTableMode ? "Switched to Hero" : "Workout Table ON");
  };
  const getTodayDate = () => {
    const now = new Date(new Date().toLocaleString("en-US", { timeZone: window._fhTimezone || "Australia/Sydney" }));
    return now.toLocaleDateString("en-CA", { timeZone: window._fhTimezone || "Australia/Sydney" });
  };
  // Open Add Block Modal
  const openAddBlockModal = () => {
    // Find next available block name
    const existingBlocks = Object.keys(currentDay.data || {}).filter(key => key.endsWith("_BLOCK"));
    const blockLetters = existingBlocks.map(block => block.charAt(0)).sort();
  
    // Find next available letter (A, B, C, D, E...)
    let nextLetter = 'A';
    for (let i = 0; i < 26; i++) {
      const letter = String.fromCharCode(65 + i); // A=65, B=66, etc.
      if (!blockLetters.includes(letter)) {
        nextLetter = letter;
        break;
      }
    }
  
    const suggestedName = `${nextLetter}_BLOCK`;
    setNewBlockName(suggestedName);
    setNewBlockTitle(suggestedName.replace("_", " "));
    setShowAddBlockModal(true);
  };
  // Add New Block
  const addNewBlock = async () => {
    if (!newBlockName.endsWith("_BLOCK")) {
      message.error("Block name must end with '_BLOCK'");
      return;
    }
  
    if (!newBlockTitle.trim()) {
      message.error("Block title is required");
      return;
    }
  
    const token = localStorage.getItem("accessToken");
    try {
      const res = await fetch(`${apiPath.baseURL}${apiPath.addBlock}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          day: activeDay,
          block_name: newBlockName,
          block_title: newBlockTitle
        })
      });
    
      const data = await res.json();
      if (data.status === 200) {
        message.success(`${newBlockName} added successfully!`);
        setShowAddBlockModal(false);
        setNewBlockName("");
        setNewBlockTitle("");
        fetchWeeklyPlan(true); // Refresh data (silent - no flicker/audio cutout)
      } else {
        message.error(data.error || "Failed to add block");
      }
    } catch (err) {
      message.error("Failed to add block");
    }
  };
  // Delete Block
  const deleteBlock = async (blockKey) => {
    setShowDeleteModal(true);
    setBlockToDelete(blockKey);
  };
  const confirmDeleteBlock = async () => {
    const token = localStorage.getItem("accessToken");
    try {
      // Pre-migrate: ensure the date entry and block exist in the backend's
      // date-keyed structure before deleting. Necessary for gyms whose data still
      // lives under legacy day-name keys (e.g. plan.weekly_plan.days.MONDAY).
      // resolveDayData only looks at fullWeek["MONDAY"] (top-level), but legacy
      // data is at fullWeek.days["MONDAY"], so we must fall back manually.
      const currentDayData = resolveDayData(fullWeekData?.full_week, activeDay);
      let blockData = currentDayData?.data?.[blockToDelete];

      // Fallback: check legacy days.DAYNAME structure if date-keyed lookup missed it
      if (!blockData && /^\d{4}-\d{2}-\d{2}$/.test(activeDay)) {
        const dayName = new Date(activeDay + 'T12:00:00')
          .toLocaleString('en-US', { weekday: 'long', timeZone: 'Australia/Sydney' })
          .toUpperCase();
        blockData = fullWeekData?.full_week?.days?.[dayName]?.data?.[blockToDelete];
      }

      if (blockData) {
        await fetch(`${apiPath.baseURL}${apiPath.updateBlock}`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            day: activeDay,
            block: blockToDelete,
            block_data: blockData,
          })
        });
      }

      const res = await fetch(`${apiPath.baseURL}${apiPath.deleteBlock}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          day: activeDay,
          block_name: blockToDelete
        })
      });

      const data = await res.json();
      if (data.status === 200) {
        message.success(`${blockToDelete} deleted successfully!`);
        fetchWeeklyPlan(true); // Refresh data (silent - no flicker/audio cutout)
      } else {
        message.error(data.error || "Failed to delete block");
      }
    } catch (err) {
      message.error("Failed to delete block");
    } finally {
      setShowDeleteModal(false);
      setBlockToDelete("");
    }
  };
  // Fallback clipboard copy for HTTP/older browsers
  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
  
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        message.success("Link copied!");
      } else {
        message.error("Copy failed");
      }
    } catch (err) {
      message.error("Copy not supported");
    }
  
    document.body.removeChild(textArea);
  };
  // Session Header - Updated with audio controls
  const SessionHeader = () => {
    const [title, setTitle] = useState(currentDay.name || "");
    const [selectedColor, setSelectedColor] = useState("#000000ff");
    const [tempColor, setTempColor] = useState(selectedColor);
    useEffect(() => setTitle(currentDay.name || ""), [currentDay]);
    useEffect(() => {
        // keep picker in sync when currentDay loads/changes
        setSelectedColor(currentDay?.color || "#004aad");
      }, [currentDay?.color, activeDay]);
     
    // Listen to WebSocket for workout state updates only
    useEffect(() => {
      if (!socket) return;
      const handler = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.day !== activeDayRef.current) return;

          // Only update workout active state, not button modes
          if (data.type === 'workout_complete' || data.type === 'workout_stopped') {
            setIsWorkoutActive(false);
            setIsWorkoutPaused(false);
            setCurrentMode("hero");
            if (data.type === 'workout_complete') {
              playBell("complete");
              workoutJustCompletedRef.current = true; // music keeps playing
            }
          } else if (data.type === 'show_countdown' || data.type === 'timer_update' || data.type === 'show_transition') {
            setIsWorkoutActive(true);
            setIsWorkoutPaused(false);
            setCurrentMode("workout");
            if (data.type === 'show_transition') playBell("transition");
            // Play workout audio when workout starts (after countdown)
            const currentMedia = resolveDayData(fullWeekDataRef.current?.full_week, activeDayRef.current)?.media || {};
            if (data.type === 'timer_update' && currentMedia.workout_audio && !audioPlaying.workout) {
              playAudio('workout');
            }
          } else if (data.type === 'pause_workout' || data.type === 'workout_paused') {
            setIsWorkoutPaused(true);
            // Pause workout audio when workout is paused
            pauseAudio('workout');
          } else if (data.type === 'resume_workout' || data.type === 'workout_resumed') {
            setIsWorkoutPaused(false);
            // Resume workout audio when workout is resumed
            const currentMedia = resolveDayData(fullWeekDataRef.current?.full_week, activeDayRef.current)?.media || {};
            if (currentMedia.workout_audio && !audioPlaying.workout) {
              playAudio('workout');
            }
          }
        } catch (err) {
        }
      };
      socket.addEventListener("message", handler);
      return () => socket.removeEventListener("message", handler);
    }, [socket, audioPlaying.workout]);
    const sendCommand = (type, payload = {}) => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        message.error("Live sync not ready");
        return false;
      }
    
      // Add default payload if empty
      if (Object.keys(payload).length === 0) {
        payload = { message: `Command ${type} for ${activeDay}` };
      }
    
      socket.send(JSON.stringify({ type, day: activeDay, payload }));
      return true;
    };
    const handleStartStopWorkout = () => {
  // Get all blocks for current day
  const allBlocks = Object.keys(currentDay.data || {}).filter(key => key.endsWith('_BLOCK')).sort();
  const completionMsg = currentDay.completion_settings?.message || "You've just completed!";
  // Calculate repetitions based on number of blocks
  const repetitions = allBlocks.length;
  if (audioPlaying.warmup) pauseAudio('warmup');
  if (audioPlaying.pre_hype) pauseAudio('pre_hype');
  
  if (isWorkoutActive) {
    if (isWorkoutPaused) {
      // Resume all blocks from current state
      allBlocks.forEach((blockKey, index) => {
        const blockData = currentDay.data[blockKey] || {};
        const duration = parseDuration(blockData.duration) || 600; // 10 minutes default
        const timeSegments = blockData.timeSegments || [];
        
        sendCommand("resume_workout", {
          day: activeDay,
          resume_all: true,
          current_block: blockKey,
          current_block_index: index,
          all_blocks: [{
            block_name: blockKey,
            duration: duration,
            timeSegments: timeSegments,
            total_seconds: duration
          }]
        });
      });
      
      setIsWorkoutPaused(false);
      // Blocks resumed
      
      const currentMedia = resolveDayData(fullWeekData?.full_week, activeDay)?.media || {};
      if (currentMedia.workout_audio && !audioPlaying.workout) {
        playAudio('workout');
      }
    } else {
      // Pause all blocks
      sendCommand("pause_workout", {
        day: activeDay,
        pause_all: true
      });
      setIsWorkoutPaused(true);
      // Blocks paused
      
      pauseAudio('workout');
    }
  } else {
    // Hyrox auto-launch: a block set to Variant C runs the station board with a
    // count-UP (elapsed) timer instead of the normal countdown workout. A Variant C
    // block renders as an empty table in normal mode, so this is strictly better.
    // Only treat this as a hyrox day when EVERY block is Variant C. A single
    // stray C block on an otherwise-normal day must not hijack START into the
    // station board (that trapped coaches on hyrox and killed the normal/B timer).
    // Intentional per-block hyrox still launches from the block's START HYROX TIMER.
    const _cBlocks = allBlocks.filter(k => ((currentDay.data[k] || {}).activeVariant === 'C'));
    const _hyroxKey = (allBlocks.length > 0 && _cBlocks.length === allBlocks.length) ? _cBlocks[0] : null;
    if (_hyroxKey) {
      const _hb = currentDay.data[_hyroxKey] || {};
      const _stations = (((_hb.variants || {}).C || {}).stations || []).filter(s => s && s.trim());
      sendWsCommand("show_table", { day: activeDay, clear_countdown: true });
      sendWsCommand("start_hyrox", { day: activeDay, block: _hyroxKey, stations: _stations });
      workoutJustCompletedRef.current = false;
      setIsWorkoutActive(true);
      setIsWorkoutPaused(false);
      setCurrentMode("workout");
      const _hmedia = resolveDayData(fullWeekData?.full_week, activeDay)?.media || {};
      if (_hmedia.workout_audio) playAudio('workout');
      return;
    }
    // Start workout for EACH block with proper data
    allBlocks.forEach((blockKey, index) => {
      const blockData = currentDay.data[blockKey] || {};
      const duration = parseDuration(blockData.duration) || 600;
      const timeSegments = blockData.timeSegments || [];
      const blockTransition = blockData.block_transition || 0;
      
      // Prepare data for ALL blocks
      const allBlocksData = allBlocks.map((key, idx) => {
        const data = currentDay.data[key] || {};
        return {
          block_name: key,
          duration: parseDuration(data.duration) || 600,
          timeSegments: data.timeSegments || [],
          total_seconds: parseDuration(data.duration) || 600,
          block_transition: data.block_transition || 0
        };
      });
      
      // Check if we should resume existing workout
      const shouldResume = isWorkoutPaused;
      const dayColor = currentDay.color || '#004aad';
      
      sendCommand("start_workout", {
        day: activeDay,
        all_blocks: allBlocksData,  // Send data for ALL blocks
        block_transition: blockTransition,
        current_block: blockKey,
        current_block_index: index,
        total_blocks: allBlocks.length,
        current_repetition: 1,
        max_repetitions: repetitions, // Dynamic: based on number of blocks
        repetition_count: 1,
        resume_existing: shouldResume,
        day_colors: dayColor,
        completion_message: completionMsg

      });
    });
    
    workoutJustCompletedRef.current = false; // new workout — clear completion flag
    setIsWorkoutActive(true);
    setIsWorkoutPaused(false);
    // Workout started

    const currentMedia = resolveDayData(fullWeekData?.full_week, activeDay)?.media || {};
    if (currentMedia.workout_audio) {
      playAudio('workout');
    }
  }
};
    const handlePreviewToggle = () => {
      if (currentMode === "table") {
        // ⚠️  DO NOT MODIFY WITHOUT DALEY'S EXPLICIT APPROVAL ⚠️
        // RESET = full reset: stops ALL audio and clears all workout state.
        // Approved behaviour (2026-04-09).
        workoutJustCompletedRef.current = false;
        stopAllAudio();
        setCurrentMode("hero");
        setIsWorkoutActive(false);
        setIsWorkoutPaused(false);
        sendCommand("show_hero");
      } else {
        // DIRECTLY SHOW TABLE — NO COUNTDOWN, NO TIMER
        setCurrentMode("table");
        // No block data embedded — Flutter TVs use their preloaded lastTableState (set when
        // operator taps their block in the TV app dashboard, same as CMN GND workflow).
        // Browser TVs use their own URL-param block (blockRef.current in WebsiteLinkPage).
        sendCommand("show_table", { day: activeDay, clear_countdown: true });
        // Table preview on — audio keeps playing
      }
    };
    const handlePreHype = () => {
      if (currentMode === "pre_hype") {
        setCurrentMode("hero");
        sendCommand("show_hero");
        pauseAudio('pre_hype');
        // Pre-hype stopped
      } else {
        const videoUrl = currentDay.media?.pre_hype_video;
        const audioUrl = currentDay.media?.pre_hype_audio;
       
        if (!videoUrl && !audioUrl) {
          message.warning("Please upload Pre-Hype Video or Audio first!");
          return;
        }
        if (audioPlaying.workout) pauseAudio('workout');
        // Stop other audio
        stopOtherAudio('pre_hype');
       
        setCurrentMode("pre_hype");
       
        if (videoUrl) {
          sendCommand("show_pre_hype", { video_url: videoUrl });
        }
       
        if (audioUrl && !audioPlaying.pre_hype) {
          playAudio('pre_hype');
        }
       
        // Pre-hype live
      }
    };
    const handleWarmup = () => {
      if (currentMode === "warmup") {
        setCurrentMode("hero");
        sendCommand("show_hero");
        pauseAudio('warmup');
        // Warm-up stopped
      } else {
        const videoUrl = currentDay.media?.warmup_video;
        const audioUrl = currentDay.media?.warmup_audio;
       
        if (!videoUrl && !audioUrl) {
          message.warning("Please upload Warm-Up Video or Audio first!");
          return;
        }
        if (audioPlaying.workout) pauseAudio('workout');
        // Stop other audio
        stopOtherAudio('warmup');
       
        setCurrentMode("warmup");
       
        if (videoUrl) {
          sendCommand("show_warmup", { video_url: videoUrl });
        }
       
        if (audioUrl && !audioPlaying.warmup) {
          playAudio('warmup');
        }
       
        // Warm-up live
      }
    };
    const handleCooldown = () => {
      if (currentMode === "cooldown") {
        setCurrentMode("hero");
        sendCommand("show_hero");
        pauseAudio('cooldown');
      } else {
        const audioUrl = currentDay.media?.cooldown_audio;

        if (!audioUrl) {
          message.warning("Please upload Cool-Down Audio first!");
          return;
        }
        if (audioPlaying.workout) pauseAudio('workout');
        workoutJustCompletedRef.current = false; // cooldown explicitly chosen — clear flag
        stopOtherAudio('cooldown');

        setCurrentMode("cooldown");
        sendCommand("show_cooldown");

        if (audioUrl && !audioPlaying.cooldown) {
          playAudio('cooldown');
        }
      }
    };
    const stopAllMedia = () => {
  stopAllAudio();
  setCurrentMode("hero");
  setIsWorkoutActive(false);
  setIsWorkoutPaused(false);
};
    const handleRestart = () => {
      workoutJustCompletedRef.current = false; // explicit reset — stop music too
      sendCommand("stop_workout", { day: activeDay, stop_all: true });
      setCurrentMode("hero");
      setIsWorkoutActive(false);
      setIsWorkoutPaused(false);
      sendCommand("show_hero", { day: activeDay, reset_all: true });
      stopAllAudio();
      setRestartFlash(true);
      setTimeout(() => setRestartFlash(false), 1500);
    };
    const handleColorChange = (color) => {
      setSelectedColor(color);
      // Persist to backend / local state through autoSave
      autoSave({ new_color: color });
      // Notify live clients via WebSocket
      if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify({ type: "update_color", day: activeDay, color }));
          // Color sent
        } catch (err) {
          message.error("Failed to send color");
        }
      } else {
        // Color updated locally
      }
    };
    

// Initialize colors from currentDay data
useEffect(() => {
  setTempTextColor(currentDay?.text_color || "#FCDC08");
  setTempBorderColor(currentDay?.border_color || "#FCDC08");
}, [currentDay?.text_color, currentDay?.border_color]);
    return (
      <div className="session-header">
        <div className="days-navigation">
          {DAYS.map((dateStr, idx) => {
            const d = new Date(dateStr + "T00:00:00");
            const label = `${DAY_LABELS_SHORT[idx]} ${d.getDate()} ${d.toLocaleString("en-AU", { month: "short" })}`;
            return (
              <button
                key={dateStr}
                className={`day-button ${dateStr === activeDay ? "active" : ""} ${dateStr === getTodayDate() ? "today-highlight" : ""}`}
                onClick={() => setActiveDay(dateStr)}
              >
                {label}
                {dateStr === getTodayDate() && " (TODAY)"}
              </button>
            );
          })}
        </div>
        <div className="session-info">
          <div className="session-title-group">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => title !== currentDay.name && autoSave({ new_title: title })}
              style={{ fontSize: 32, fontWeight: "bold", color: "#004aad", background: "transparent", border: "2px solid transparent", borderRadius: 12, padding: "8px 16px", minWidth: 250 }}
              placeholder="Workout Name"
            />
          </div>
          {/* ====== COLOR PICKER ====== */}
          {/* <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <input
              type="color"
              value={selectedColor}
              onChange={(e) => setTempColor(e.target.value)} // temporary color store
              style={{ width: 40, height: 40, borderRadius: 8, border: "1px solid #333", cursor: "pointer" }}
              title="Select theme color for this day"
            />
            <button
              onClick={() => handleColorChange(tempColor)}
              style={{
                padding: "6px 12px",
                background: "#444",
                border: "1px solid #666",
                borderRadius: 6,
                color: "#fff",
                cursor: "pointer",
                fontWeight: "600"
              }}
            >
              Apply
            </button>
            <div style={{ color: "#ccc", fontWeight: "600" }}>Theme Color</div>
          </div> */}
          
          {/* ACTION BUTTONS */}
          <div className="session-actions" style={{ gap: 12, display: "flex", flexWrap: "wrap", justifyContent: "center" }}>

            {[
              { label: currentMode === "table" ? "RESET" : "PREVIEW", onClick: handlePreviewToggle, active: currentMode === "table", w: 120 },
              { label: "RESTART", onClick: handleRestart, active: restartFlash, w: 120 },
              { label: currentMode === "pre_hype" ? "STOP" : "PRE-HYPE", onClick: handlePreHype, active: currentMode === "pre_hype", w: 130, audio: media.pre_hype_audio && (audioPlaying.pre_hype ? <Volume2 size={14} /> : <VolumeX size={14} />) },
              { label: currentMode === "warmup" ? "STOP" : "WARM-UP", onClick: handleWarmup, active: currentMode === "warmup", w: 130, audio: media.warmup_audio && (audioPlaying.warmup ? <Volume2 size={14} /> : <VolumeX size={14} />) },
              { label: isWorkoutActive ? "STOP" : "START", onClick: handleStartStopWorkout, active: isWorkoutActive, w: 110, audio: media.workout_audio && isWorkoutActive && !isWorkoutPaused && (audioPlaying.workout ? <Volume2 size={14} /> : <VolumeX size={14} />) },
              { label: currentMode === "cooldown" ? "STOP" : "COOL-DOWN", onClick: handleCooldown, active: currentMode === "cooldown", w: 130, audio: media.cooldown_audio && (audioPlaying.cooldown ? <Volume2 size={14} /> : <VolumeX size={14} />) },
              { label: "\u2699 SETTINGS", onClick: () => setShowSettings(true), active: showSettings, w: 120 },
            ].map((btn, i) => (
              <button
                key={i}
                onClick={btn.onClick}
                style={{
                  width: btn.w, padding: "10px 8px", border: "none", borderRadius: 8, cursor: "pointer",
                  fontWeight: "700", fontSize: 13, color: "#fff", transition: "all 0.2s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  background: btn.active ? "linear-gradient(135deg, #004aad 0%, #003d8f 100%)" : "rgba(255, 255, 255, 0.05)",
                  border: btn.active ? "1px solid rgba(0, 74, 173, 0.4)" : "1px solid rgba(255, 255, 255, 0.08)",
                  boxShadow: btn.active ? "0 4px 14px rgba(0, 74, 173, 0.3)" : "none",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  borderRadius: 12,
                  letterSpacing: "0.06em",
                }}
              >
                {btn.label}
                {btn.audio && <span>{btn.audio}</span>}
              </button>
            ))}

          </div>


        </div>
      </div>
    );
  };
  // Workout Block
  const WorkoutBlock = ({ block, blockKey, sendWsCommand }) => {
    // Use cached block data if available (persists through re-mounts)
    const cachedBlock = blockDataCacheRef.current[`${activeDay}_${blockKey}`] || block;

    // A/B variant system — existing tasks become variant A for backward compatibility
    const initVariants = () => {
      if (cachedBlock.variants) {
        const v = cachedBlock.variants;
        // Guard: if variants exists but has no A/B/C keys (e.g. legacy 'active' key),
        // migrate 'active' → 'A' so the component doesn't crash on undefined.map()
        if (v.A === undefined && v.B === undefined && v.C === undefined) {
          if (v.active !== undefined) v.A = v.active;
          else v.A = (cachedBlock.tasks || block.tasks || []);
          delete v.active;
        }
        if (!v.C) v.C = { stations: [], mode: "hyrox" };
        if (!Array.isArray(v.A)) v.A = Array.isArray(cachedBlock.tasks) ? cachedBlock.tasks : (Array.isArray(block.tasks) ? block.tasks : []);
        if (!Array.isArray(v.B)) v.B = [];
        return v;
      }
      const tasks = (Array.isArray(cachedBlock.tasks) ? cachedBlock.tasks : Array.isArray(block.tasks) ? block.tasks : []).map((t, i) => ({
        id: t.id || `${blockKey}-${i}`, name: t.name || "", sets: t.sets || "",
        reps: t.reps || "", tempo: t.tempo || "", isHeader: t.isHeader || false, isSplit: t.isSplit || false,
      }));
      return { A: tasks, B: [], C: { stations: [], mode: "hyrox" } };
    };
    const [variants, setVariants] = useState(initVariants);
    const [activeVariant, setActiveVariant] = useState(cachedBlock.activeVariant || "A");
    const [exercises, setExercises] = useState(() => {
      const av = cachedBlock.activeVariant || "A";
      if (av === "C") return initVariants().A.map ? initVariants().A.map((t, i) => ({
        id: t.id || `${blockKey}-${i}`, name: t.name || "", sets: t.sets || "",
        reps: t.reps || "", tempo: t.tempo || "", isHeader: t.isHeader || false, isSplit: t.isSplit || false,
      })) : [];
      return (initVariants()[av] || initVariants().A).map((t, i) => ({
        id: t.id || `${blockKey}-${i}`, name: t.name || "", sets: t.sets || "",
        reps: t.reps || "", tempo: t.tempo || "", isHeader: t.isHeader || false, isSplit: t.isSplit || false,
      }));
    });

    const [stations, setStations] = useState(Array.isArray(cachedBlock.variants?.C?.stations) ? cachedBlock.variants.C.stations : []);

    // Switch variant: save current exercises to current variant, load other
    const switchVariant = (newVariant) => {
      if (newVariant === activeVariant) return;
      // Save current variant data
      let updatedVariants;
      if (activeVariant === "C") {
        updatedVariants = { ...variants, C: { stations, mode: "hyrox" } };
      } else {
        updatedVariants = { ...variants, [activeVariant]: exercises.map(ex => ({ ...ex })) };
      }
      // Load new variant
      if (newVariant === "C") {
        setStations(updatedVariants.C?.stations || []);
      } else {
        const otherTasks = (updatedVariants[newVariant] || []).map((t, i) => ({
          id: t.id || `${blockKey}-${newVariant}-${i}`, name: t.name || "", sets: t.sets || "",
          reps: t.reps || "", tempo: t.tempo || "", isHeader: t.isHeader || false, isSplit: t.isSplit || false,
        }));
        setExercises(otherTasks);
      }
      setVariants(updatedVariants);
      setActiveVariant(newVariant);
      // Save immediately
      const saveTasks = newVariant === "C" ? exercises : (updatedVariants[newVariant] || []).map((t, i) => ({
        id: t.id || `${blockKey}-${newVariant}-${i}`, name: t.name || "", sets: t.sets || "",
        reps: t.reps || "", tempo: t.tempo || "", isHeader: t.isHeader || false, isSplit: t.isSplit || false,
      }));
      debounceSave(saveTasks, blockDuration, timeSegments, notes, updatedVariants, newVariant);
    };
    // Initialize duration as MM:SS format
    const [blockDuration, setBlockDuration] = useState(() => {
      const duration = block.duration;
      if (typeof duration === 'string' && duration.includes(':')) {
        return duration; // Already in MM:SS format
      } else if (duration) {
        // Convert number of minutes to MM:SS format
        const minutes = parseInt(duration) || 40;
        return `${minutes}:00`;
      }
      return "40:00"; // Default
    });
    const [notes, setNotes] = useState(block.cooldown || "");
    const [showDurationModal, setShowDurationModal] = useState(false);
    const [tempDurationMinutes, setTempDurationMinutes] = useState("40");
    const [tempDurationSeconds, setTempDurationSeconds] = useState("00");
    const [blockTransition, setBlockTransition] = useState(block.block_transition || 0);
    const [timeSegments, setTimeSegments] = useState(() => {
      // If backend has timeSegments, use them, otherwise create default
      if (block.timeSegments && block.timeSegments.length > 0) {
        // Ensure backward compatibility - convert old format to new format
        return block.timeSegments.map(segment => {
          if (segment.workingTime !== undefined) {
            return segment; // New format
          } else {
            // Old format - convert duration to workingTime
            return {
              workingTime: segment.duration || 2400, // 40 minutes default
              restTime: 0,
              duration: segment.duration || 2400,
              label: segment.label || '40:00'
            };
          }
        });
      }
      const defaultDuration = parseDuration(blockDuration);
      return [{
        workingTime: defaultDuration,
        restTime: 0,
        duration: defaultDuration,
        label: formatDuration(defaultDuration)
      }];
    });
    const [showSegmentModal, setShowSegmentModal] = useState(false);
    const [currentSegments, setCurrentSegments] = useState([]);
    const [blockName, setBlockName] = useState(block.blockName || block.name || "EXERCISE");
    const [showSets, setShowSets] = useState(cachedBlock.showSets !== undefined ? cachedBlock.showSets : block.showSets !== false);
    const [showReps, setShowReps] = useState(cachedBlock.showReps !== undefined ? cachedBlock.showReps : block.showReps !== false);
    const [showTempo, setShowTempo] = useState(cachedBlock.showTempo !== undefined ? cachedBlock.showTempo : block.showTempo !== false);

    // NEW: Use refs for debouncing to prevent multiple API calls
    const saveTimeoutRef = useRef(null);
    const lastSaveDataRef = useRef(null);
   
    // Debug log and update blockName when block changes
    useEffect(() => {
      const newBlockName = block.blockName || block.name || "EXERCISE";
      if (newBlockName !== blockName) {
        setBlockName(newBlockName);
      }
    }, [block.blockName, block.name]);
   
    // NEW: Improved debounce save function
    const debounceSave = (updatedExercises, duration, segments, notesValue, variantsOverride, variantOverride) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      const currentTasks = (updatedExercises || exercises).map(ex => ({ ...ex }));
      const currentVariant = variantOverride || activeVariant;
      const currentVariants = variantsOverride || { ...variants, [activeVariant]: currentTasks };

      const saveData = {
        block: blockKey,
        block_data: {
          title: block.title,
          blockName: blockName,
          duration: duration || blockDuration,
          timeSegments: segments || timeSegments,
          cooldown: notesValue || notes,
          tasks: currentTasks,
          block_transition: blockTransition || 0,
          showSets,
          showReps,
          showTempo,
          variants: currentVariants,
          activeVariant: currentVariant,
        }
      };
     
      // Update cache immediately so re-mounts always get latest state
      blockDataCacheRef.current[`${activeDay}_${blockKey}`] = saveData.block_data;

      // Store for comparison
      lastSaveDataRef.current = JSON.stringify(saveData);
     
      // Set new timeout (500ms — near real-time saves)
      saveTimeoutRef.current = setTimeout(() => {
        autoSave(saveData);
      }, 500);
    };
   
    // Flush pending save on unmount (don't lose data)
    useEffect(() => {
      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          // Fire the save immediately with cached data
          const cached = blockDataCacheRef.current[`${activeDay}_${blockKey}`];
          if (cached) {
            autoSave({ block: blockKey, block_data: cached });
          }
        }
      };
    }, []);
   
    const handleChange = (id, field, value) => {
      const updated = exercises.map(ex => ex.id === id ? { ...ex, [field]: value } : ex);
      setExercises(updated);
      debounceSave(updated, blockDuration, timeSegments, notes);
    };
   
    const handleDurationChange = (value) => {
      // Validate and format duration input
      let formattedValue = value;
      
      // Allow only numbers and colon
      if (!/^[\d:]*$/.test(value)) return;
      
      // Handle manual editing of duration
      if (value.includes(':')) {
        const [minutes, seconds] = value.split(':');
        const mins = parseInt(minutes) || 0;
        const secs = parseInt(seconds) || 0;
        
        // Ensure seconds are between 0-59
        const validSecs = Math.min(59, Math.max(0, secs));
        
        formattedValue = `${mins}:${validSecs.toString().padStart(2, '0')}`;
      } else {
        // If no colon, treat as minutes
        const minutes = parseInt(value) || 40;
        formattedValue = `${minutes}:00`;
      }
      
      setBlockDuration(formattedValue);
      
      // Reset segments when duration changes directly
      const newSegments = [{
        workingTime: parseDuration(formattedValue),
        restTime: 0,
        duration: parseDuration(formattedValue),
        label: formattedValue
      }];
      setTimeSegments(newSegments);
      debounceSave(exercises, formattedValue, newSegments, notes);
    };
   
    const openDurationModal = () => {
      const [minutes, seconds] = blockDuration.split(':');
      setTempDurationMinutes(minutes);
      setTempDurationSeconds(seconds);
      setShowDurationModal(true);
    };
   
    const saveDuration = () => {
      const oldDuration = blockDuration;
      const newDuration = `${parseInt(tempDurationMinutes) || 0}:${(parseInt(tempDurationSeconds) || 0).toString().padStart(2, '0')}`;
    
      // Reset to single segment when duration changes via modal
      const newSegments = [{
        workingTime: parseDuration(newDuration),
        restTime: 0,
        duration: parseDuration(newDuration),
        label: newDuration
      }];
      setTimeSegments(newSegments);
      setBlockDuration(newDuration);
    
      debounceSave(exercises, newDuration, newSegments, notes);
      setShowDurationModal(false);
      // Duration updated
    };
   
    const openSegmentModal = () => {
      setCurrentSegments([...timeSegments]);
      setShowSegmentModal(true);
    };
   
    const addSegment = () => {
      setCurrentSegments([...currentSegments, {workingTime: 60, restTime: 0, duration: 60, label: '01:00'}]);
    };
   
    const updateSegment = (index, type, field, value) => {
      // field is 'minutes' or 'seconds' — read current state directly to avoid stale closures
      const updated = [...currentSegments];
      if (!updated[index]) {
        updated[index] = { workingTime: 60, restTime: 0, label: '01:00' };
      }

      const currentTime = type === 'working' ? (updated[index].workingTime || 0) : (updated[index].restTime || 0);
      const currentMinutes = Math.floor(currentTime / 60);
      const currentSeconds = currentTime % 60;

      const newMinutes = field === 'minutes' ? value : currentMinutes;
      const newSeconds = field === 'seconds' ? value : currentSeconds;
      const totalSeconds = (newMinutes * 60) + newSeconds;

      if (type === 'working') {
        updated[index].workingTime = totalSeconds;
      } else {
        updated[index].restTime = totalSeconds;
      }
    
      // Create better label that shows both working and rest time
      const workingMinutes = Math.floor(updated[index].workingTime / 60);
      const workingSeconds = updated[index].workingTime % 60;
      const restMinutes = Math.floor(updated[index].restTime / 60);
      const restSeconds = updated[index].restTime % 60;
    
      let label = '';
      if (workingMinutes > 0 || workingSeconds > 0) {
        label += `${workingMinutes.toString().padStart(2, '0')}:${workingSeconds.toString().padStart(2, '0')}`;
      }
    
      if (updated[index].restTime > 0) {
        label += ' + ';
        if (restMinutes > 0 || restSeconds > 0) {
          label += `${restMinutes.toString().padStart(2, '0')}:${restSeconds.toString().padStart(2, '0')}`;
        }
        label += 'R';
      }
    
      updated[index].label = label || '01:00';
    
      // Total duration is working + rest time
      updated[index].duration = updated[index].workingTime + updated[index].restTime;
    
      setCurrentSegments(updated);
    };
   
    const removeSegment = (index) => {
      setCurrentSegments(currentSegments.filter((_, i) => i !== index));
    };
   
    const saveSegments = () => {
      // Validate that all segments have working time
      const invalidSegments = currentSegments.filter(seg => !seg.workingTime || seg.workingTime <= 0);
      if (invalidSegments.length > 0) {
        message.error('All segments must have working time greater than 0');
        return;
      }
    
      let finalSegments = [...currentSegments];

      // Calculate total time from segments — this dictates the block duration
      const totalSegmentSeconds = finalSegments.reduce((sum, seg) => {
        return sum + ((seg.workingTime || 0) + (seg.restTime || 0));
      }, 0);

      // Update block duration to match segment total
      const newDuration = formatDuration(totalSegmentSeconds > 0 ? totalSegmentSeconds : 0);
      setBlockDuration(newDuration);

      setTimeSegments(finalSegments);
      setShowSegmentModal(false);

      debounceSave(exercises, newDuration, finalSegments, notes);
    };
   
    const handleAdd = () => {
      const newEx = { id: Date.now().toString(), name: "", sets: "", reps: "", tempo: "" };
      const updated = [...exercises, newEx];
      setExercises(updated);
      debounceSave(updated, blockDuration, timeSegments, notes);
    };
   
    const handleDelete = (id) => {
      const updated = exercises.filter(ex => ex.id !== id);
      setExercises(updated);
      debounceSave(updated, blockDuration, timeSegments, notes);
    };
   
    // NEW: Block name change handler with debouncing
    const handleBlockNameChange = (e) => {
      const newName = e.target.value;
      setBlockName(newName);
     
      // Debounce the save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
     
      saveTimeoutRef.current = setTimeout(() => {
        autoSave({
          block: blockKey,
          block_data: {
            title: block.title,
            blockName: newName,
            duration: blockDuration,
            timeSegments: timeSegments,
            cooldown: notes,
            tasks: exercises.map(ex => ({ ...ex })),
            block_transition: blockTransition || 0,
            showSets, showReps, showTempo,
          }
        });
      }, 500);
    };
    // NEW: Notes change handler with debouncing
    const handleNotesChange = (e) => {
      
      const newNotes = e.target.value;
      setNotes(newNotes);
      debounceSave(exercises, blockDuration, timeSegments, newNotes);
    };
    // NEW: Block transition change handler with debouncing
    const handleBlockTransitionChange = (value) => {
      const newTransition = parseInt(value) || 0;
      setBlockTransition(newTransition);
      debounceSave(exercises, blockDuration, timeSegments, notes);
    };
    return (
      <div className="workout-block">
        <div className="workout-block-header">
       <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
           <h2 className="workout-block-title" style={{ margin: 0, flexShrink: 0 }}>{block.title}</h2>
           <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <Timer size={20} color="#fcdc08" />
            <span style={{ fontWeight: "700", fontFamily: "monospace", fontSize: "22px", color: "#fcdc08", letterSpacing: "0.02em" }} title="Duration is calculated from segments">{blockDuration}</span>
          </div>
          <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 20, flexShrink: 0 }}>|</span>
          <Input
            value={blockName}
            onChange={handleBlockNameChange}
            placeholder="Block description..."
            style={{ flex: 1, minWidth: 120, fontSize: 15, fontWeight: "600", background: "transparent", border: "none", color: "rgba(255,255,255,0.7)", padding: "4px 8px" }}
          />
          <div style={{ display: "flex", gap: 2, flexShrink: 0, marginLeft: 8 }}>
            {["A", "B", "C"].map(v => (
              <button key={v} onClick={() => switchVariant(v)} style={{
                width: 32, height: 28, border: "none",
                borderRadius: v === "A" ? "6px 0 0 6px" : v === "C" ? "0 6px 6px 0" : "0",
                background: activeVariant === v ? (v === "C" ? "#b8860b" : "#004aad") : "rgba(255,255,255,0.08)",
                color: activeVariant === v ? "#fff" : "rgba(255,255,255,0.4)",
                fontWeight: 800, fontSize: 13, cursor: "pointer", transition: "all 0.2s",
                letterSpacing: "0.02em",
              }}>{v}</button>
            ))}
          </div>
       </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className="edit-button"
              onClick={() => {
                const token = localStorage.getItem("accessToken");
                const url = `${window.location.origin}/stream-section?day=${activeDay}&block=${blockKey}&token=${token}`;
                window.open(url, '_blank');
              }}
            >
              <Link size={16} />
            </button>
            <button
              className="edit-button"
              onClick={() => deleteBlock(blockKey)}
              style={{ background: "#e74c3c" }}
              title="Delete Block"
            >
              <Trash2 size={16} />
            </button>
          </div>
        
        </div>
        <div className="workout-content">
          <div className="table-section-table">
            {activeVariant === "C" ? (
              <div style={{ padding: "16px 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <span style={{ color: "#fcdc08", fontWeight: 800, fontSize: 16, letterSpacing: "0.05em" }}>HYROX MODE</span>
                  <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 12 }}>Stations / Rounds</span>
                </div>
                {stations.map((station, idx) => (
                  <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                    <span style={{ color: "#fcdc08", fontWeight: 700, fontSize: 14, minWidth: 30 }}>{idx + 1}.</span>
                    <Input value={station} onChange={(e) => {
                      const updated = [...stations];
                      updated[idx] = e.target.value;
                      setStations(updated);
                      const updatedVariants = { ...variants, C: { stations: updated, mode: "hyrox" } };
                      setVariants(updatedVariants);
                      debounceSave(exercises, blockDuration, timeSegments, notes, updatedVariants, activeVariant);
                    }} placeholder="e.g. 1KM ROW" style={{ flex: 1 }} />
                    <button onClick={() => {
                      const updated = stations.filter((_, i) => i !== idx);
                      setStations(updated);
                      const updatedVariants = { ...variants, C: { stations: updated, mode: "hyrox" } };
                      setVariants(updatedVariants);
                      debounceSave(exercises, blockDuration, timeSegments, notes, updatedVariants, activeVariant);
                    }} style={{ background: "transparent", border: "none", color: "#e74c3c", cursor: "pointer", padding: 4 }}><Trash2 size={16} /></button>
                  </div>
                ))}
                <button onClick={() => {
                  const updated = [...stations, ""];
                  setStations(updated);
                  const updatedVariants = { ...variants, C: { stations: updated, mode: "hyrox" } };
                  setVariants(updatedVariants);
                  debounceSave(exercises, blockDuration, timeSegments, notes, updatedVariants, activeVariant);
                }} className="add-button" style={{ marginTop: 8 }}>
                  <Plus size={16} /> Add Station
                </button>
                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  <button
                    onClick={() => {
                      if (isWorkoutActive) {
                        sendWsCommand("stop_hyrox", { day: activeDay, block: blockKey });
                        setIsWorkoutActive(false);
                      } else {
                        sendWsCommand("show_table", { day: activeDay, clear_countdown: true });
                        sendWsCommand("start_hyrox", { day: activeDay, block: blockKey, stations: stations.filter(s => s.trim()) });
                        setIsWorkoutActive(true);
                        setCurrentMode("workout");
                      }
                    }}
                    style={{
                      padding: "10px 24px", border: "none", borderRadius: 8, cursor: "pointer",
                      fontWeight: 700, fontSize: 14, color: "#fff", letterSpacing: "0.06em",
                      background: isWorkoutActive ? "#e74c3c" : "linear-gradient(135deg, #b8860b 0%, #daa520 100%)",
                      boxShadow: isWorkoutActive ? "none" : "0 4px 14px rgba(184, 134, 11, 0.3)",
                    }}
                  >
                    {isWorkoutActive ? "STOP TIMER" : "START HYROX TIMER"}
                  </button>
                </div>
              </div>
            ) : (
            <table className="exercise-table">
              <thead>
                <tr>
                  <th>EXERCISE</th>
                  {[
                    { label: "SETS", on: showSets, set: setShowSets, key: "showSets" },
                    { label: "REPS", on: showReps, set: setShowReps, key: "showReps" },
                    { label: "TEMPO", on: showTempo, set: setShowTempo, key: "showTempo" },
                  ].map(col => (
                    <th key={col.label} className="center narrow">
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                        <span style={{ opacity: col.on ? 1 : 0.4 }}>{col.label}</span>
                        <div
                          onClick={() => {
                            const v = !col.on;
                            col.set(v);
                            const colState = { showSets, showReps, showTempo, [col.key]: v };
                            autoSave({ block: blockKey, block_data: { ...block, ...colState, tasks: exercises, blockName, duration: blockDuration, timeSegments, block_transition: blockTransition } });
                          }}
                          style={{
                            width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                            background: col.on ? "#2ecc71" : "rgba(255,255,255,0.15)",
                            transition: "background 0.25s ease",
                            position: "relative", flexShrink: 0,
                          }}
                        >
                          <div style={{
                            width: 16, height: 16, borderRadius: "50%", background: "#fff",
                            position: "absolute", top: 2,
                            left: col.on ? 18 : 2,
                            transition: "left 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                          }} />
                        </div>
                      </div>
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
  {exercises.map((ex, idx) => (
    <React.Fragment key={ex.id}>
    <tr style={{ background: ex.isHeader ? "rgba(0, 74, 173, 0.12)" : "transparent" }}>
      <td style={ex.isHeader && idx > 0 ? { paddingTop: 28 } : {}}><Input value={ex.name} onChange={(e) => handleChange(ex.id, "name", e.target.value)} placeholder={ex.isHeader ? "Section Header" : "Exercise"} style={ex.isHeader ? { fontWeight: "700", color: "#fcdc08" } : {}} /></td>
      <td className="center" style={{ opacity: showSets && !ex.isHeader ? 1 : 0.15, transition: "opacity 0.2s", ...(ex.isHeader && idx > 0 ? { paddingTop: 28 } : {}) }}><Input value={ex.isHeader ? "" : ex.sets} onChange={(e) => handleChange(ex.id, "sets", e.target.value)} style={{ width: 50 }} disabled={!showSets || ex.isHeader} /></td>
      <td className="center" style={{ opacity: showReps && !ex.isHeader ? 1 : 0.15, transition: "opacity 0.2s", ...(ex.isHeader && idx > 0 ? { paddingTop: 28 } : {}) }}><Input value={ex.isHeader ? "" : ex.reps} onChange={(e) => handleChange(ex.id, "reps", e.target.value)} style={{ width: 60 }} disabled={!showReps || ex.isHeader} /></td>
      <td className="center" style={{ opacity: showTempo && !ex.isHeader ? 1 : 0.15, transition: "opacity 0.2s", ...(ex.isHeader && idx > 0 ? { paddingTop: 28 } : {}) }}><Input value={ex.isHeader ? "" : ex.tempo} onChange={(e) => handleChange(ex.id, "tempo", e.target.value)} style={{ width: 60 }} disabled={!showTempo || ex.isHeader} /></td>
      <td className="center">
        <div style={{ display: "flex", gap: "8px", justifyContent: "center", alignItems: "center" }}>
          <button 
            className="icon-button"
            onClick={() => {
              // Create a duplicate of THIS specific exercise
              const newExercise = {
                ...ex,  // Use the current exercise 'ex' from the map
                id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
              };
              
              // Find the current index to insert after it
              const currentIndex = exercises.findIndex(e => e.id === ex.id);
              const updated = [
                ...exercises.slice(0, currentIndex + 1),
                newExercise,
                ...exercises.slice(currentIndex + 1)
              ];
              
              setExercises(updated);
              debounceSave(updated, blockDuration, timeSegments, notes);
            }}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#3498db",
              padding: "4px"
            }}
            title="Duplicate"
          >
            <Plus size={16} />
          </button>
          
          <button 
            className="icon-button" 
            onClick={() => handleDelete(ex.id)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#e74c3c",
              padding: "4px"
            }}
            title="Delete"
          >
            <Trash2 size={16} />
          </button>
          <div
            onClick={() => handleChange(ex.id, "isHeader", !ex.isHeader)}
            title={ex.isHeader ? "Remove header style" : "Make this a section header"}
            style={{
              width: 20, height: 20, borderRadius: 4, cursor: "pointer",
              border: ex.isHeader ? "2px solid #fcdc08" : "2px solid rgba(255,255,255,0.2)",
              background: ex.isHeader ? "#fcdc08" : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s ease", flexShrink: 0,
            }}
          >
            {ex.isHeader && <span style={{ color: "#000", fontSize: 12, fontWeight: "900", lineHeight: 1 }}>H</span>}
          </div>
          <div
            onClick={() => handleChange(ex.id, "isSplit", !ex.isSplit)}
            title={ex.isSplit ? "Remove split point" : "Split screen starts here"}
            style={{
              width: 20, height: 20, borderRadius: 4, cursor: "pointer",
              border: ex.isSplit ? "2px solid #004aad" : "2px solid rgba(255,255,255,0.2)",
              background: ex.isSplit ? "#004aad" : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s ease", flexShrink: 0,
            }}
          >
            {ex.isSplit && <span style={{ color: "#fff", fontSize: 10, fontWeight: "900", lineHeight: 1 }}>&#x21D4;</span>}
          </div>
        </div>
      </td>
    </tr>
    </React.Fragment>
  ))}
  <tr>
    <td colSpan="5" className="add-row">
      <button className="add-button" onClick={handleAdd}>
        <Plus size={16} /> Add Exercise
      </button>
    </td>
  </tr>
</tbody>
            </table>
            )}
            <div className="progress-bar-container">
              <div className="clock-outline-btn">
                <button
                  onClick={openDurationModal}
                  style={{
                    background: "transparent",
                    border: "2px solid #004aad",
                    borderRadius: "8px",
                    padding: "8px",
                    cursor: "pointer",
                    color: "#a8a8a8"
                  }}
                >
                  <Clock size={20} />
                </button>
              </div>
              <div className="progress-bar-fill" style={{ display: "flex", width: "100%" , gap:"5px" }}>
                {timeSegments.map((segment, index) => {
                  // Each segment width should include BOTH working + rest time
                  const segmentTotalSeconds = (segment.workingTime || 0) + (segment.restTime || 0);
                  const blockTotalSeconds = parseDuration(blockDuration);
                  const segmentWidth = (segmentTotalSeconds / blockTotalSeconds) * 100;
                
                  return (
                    <button
                      key={index}
                      onClick={openSegmentModal}
                    >
                      {/* Show working time + rest time in label */}
                      {segment.label}
                      {segment.restTime > 0 && (
                        <div style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: "3px",
                          background: "#FF9800",
                          opacity: 0.7
                        }} />
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="progress-bar-total" style={{
                color: "#a8a8a8",
                fontWeight: "bold",
                fontSize: "16px",
                padding:"8px",
                fontFamily: 'monospace'
              }}>
                {blockDuration}
              </div>
            </div>
            {/* Duration Modal - UPDATED FOR MINUTES AND SECONDS */}
            {showDurationModal && (
              <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000
              }}>
                <div style={{
                  background: "rgba(26, 26, 46, 0.6)",
                  padding: "30px",
                  borderRadius: "12px",
                  border: "2px solid #004aad",
                  minWidth: "300px",
                  textAlign: "center"
                }}>
                  <h3 style={{ color: "#fcdc08", marginBottom: "20px" }}>Set {blockKey} Duration</h3>
                  <div style={{ marginBottom: "10px", color: "#ccc", fontFamily: 'monospace', fontSize: '18px' }}>
                    Current: {blockDuration}
                  </div>
                  <div style={{ 
                    marginBottom: "20px", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    gap: "10px"
                  }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <label style={{ color: "#ccc", marginBottom: "5px" }}>Minutes</label>
                      <Input
                        type="number"
                        value={tempDurationMinutes}
                        onChange={(e) => setTempDurationMinutes(e.target.value)}
                        style={{
                          width: "80px",
                          textAlign: "center",
                          fontSize: "20px",
                          fontWeight: "bold",
                          border: "2px solid #004aad"
                        }}
                        min={0}
                        max={120}
                      />
                    </div>
                    <div style={{ fontSize: "24px", color: "#fcdc08", marginTop: "15px" }}>:</div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <label style={{ color: "#ccc", marginBottom: "5px" }}>Seconds</label>
                      <Input
                        type="number"
                        value={tempDurationSeconds}
                        onChange={(e) => {
                          let value = e.target.value;
                          if (value.length > 2) value = value.slice(0, 2);
                          if (parseInt(value) > 59) value = "59";
                          setTempDurationSeconds(value.padStart(2, '0'));
                        }}
                        style={{
                          width: "80px",
                          textAlign: "center",
                          fontSize: "20px",
                          fontWeight: "bold",
                          border: "2px solid #004aad"
                        }}
                        min={0}
                        max={59}
                      />
                    </div>
                  </div>
                  <div style={{ 
                    marginBottom: "15px", 
                    color: "#fcdc08",
                    fontSize: "14px",
                    fontFamily: 'monospace'
                  }}>
                    New duration will be: {parseInt(tempDurationMinutes) || 0}:{(parseInt(tempDurationSeconds) || 0).toString().padStart(2, '0')}
                  </div>
                  <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                    <button
                      onClick={() => setShowDurationModal(false)}
                      style={{
                        padding: "8px 16px",
                        background: "#666",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer"
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveDuration}
                      style={{
                        padding: "8px 16px",
                        background: "#004aad",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer"
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Time Segmentation Modal */}
            {showSegmentModal && (
                  <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0,0,0,0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1001
                  }}>
                    <div style={{
                      background: "rgba(26, 26, 46, 0.6)",
                      padding: "30px",
                      borderRadius: "12px",
                      border: "2px solid #004aad",
                      minWidth: "500px",
                      maxHeight: "80vh",
                      overflowY: "auto"
                    }}>
                      <h3 style={{ color: "#fcdc08", marginBottom: "20px" }}>Create Time Segments</h3>
                    
                      <div style={{ marginBottom: "20px" }}>
                        {currentSegments.map((segment, index) => {
                          const workingMinutes = Math.floor((segment.workingTime || 0) / 60);
                          const workingSeconds = (segment.workingTime || 0) % 60;
                          const restMinutes = Math.floor((segment.restTime || 0) / 60);
                          const restSeconds = (segment.restTime || 0) % 60;
                          return (
                            <div key={index} style={{
                              marginBottom: "15px",
                              padding: "15px",
                              background: "rgba(255, 255, 255, 0.03)",
                              borderRadius: "8px",
                              border: "1px solid #004aad"
                            }}>
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "10px"
                              }}>
                                <span style={{ color: "#fcdc08", fontWeight: "bold", fontSize: "16px" }}>Segment {index + 1}</span>
                                <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                  onClick={() => {
                                    const duplicate = { ...currentSegments[index] };
                                    const updated = [...currentSegments];
                                    updated.splice(index + 1, 0, duplicate);
                                    setCurrentSegments(updated);
                                  }}
                                  style={{
                                    background: "#3498db",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    padding: "5px 10px",
                                    cursor: "pointer",
                                    fontSize: "12px"
                                  }}
                                >
                                  Duplicate
                                </button>
                                <button
                                  onClick={() => removeSegment(index)}
                                  style={{
                                    background: "#e74c3c",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    padding: "5px 10px",
                                    cursor: "pointer",
                                    fontSize: "12px"
                                  }}
                                >
                                  Remove
                                </button>
                                </div>
                              </div>

                              {/* Working Time */}
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                marginBottom: "10px",
                                padding: "8px",
                                background: "#3a3a5c",
                                borderRadius: "6px",
                                border: "1px solid #004aad"
                              }}>
                                <span style={{ color: "#fcdc08", fontWeight: "bold", minWidth: "80px" }}>Working:</span>
                                <Input
                                  type="number"
                                  value={workingMinutes}
                                  onChange={(e) => updateSegment(index, 'working', 'minutes', parseInt(e.target.value) || 0)}
                                  style={{ width: "60px" }}
                                  min={0}
                                  max={60}
                                />
                                <span style={{ color: "#ccc" }}>min</span>
                                <Input
                                  type="number"
                                  value={workingSeconds}
                                  onChange={(e) => updateSegment(index, 'working', 'seconds', parseInt(e.target.value) || 0)}
                                  style={{ width: "60px" }}
                                  min={0}
                                  max={59}
                                />
                                <span style={{ color: "#ccc" }}>sec</span>
                              </div>
                            
                              {/* Rest Time */}
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "8px",
                                background: "#3a3a5c",
                                borderRadius: "6px",
                                border: "1px solid #FF9800"
                              }}>
                                <span style={{ color: "#FF9800", fontWeight: "bold", minWidth: "80px" }}>Rest:</span>
                                <Input
                                  type="number"
                                  value={restMinutes}
                                  onChange={(e) => updateSegment(index, 'rest', 'minutes', parseInt(e.target.value) || 0)}
                                  style={{ width: "60px" }}
                                  min={0}
                                  max={60}
                                />
                                <span style={{ color: "#ccc" }}>min</span>
                                <Input
                                  type="number"
                                  value={restSeconds}
                                  onChange={(e) => updateSegment(index, 'rest', 'seconds', parseInt(e.target.value) || 0)}
                                  style={{ width: "60px" }}
                                  min={0}
                                  max={59}
                                />
                                <span style={{ color: "#ccc" }}>sec</span>
                              </div>
                            </div>
                          );
                        })}
                      
                        <button
                          onClick={addSegment}
                          style={{
                            background: "#3498db",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            padding: "8px 16px",
                            cursor: "pointer",
                            marginTop: "10px"
                          }}
                        >
                          + Add Segment
                        </button>
                    {/* Block Transition */}
                    <div
                      style={{
                        marginTop: "20px",
                        padding: "10px",
                        background: "#3a3a5c",
                        borderRadius: "8px",
                        border: "1px solid #004aad",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px"
                      }}
                    >
                      <span style={{ color: "#fcdc08", fontWeight: "bold", fontSize: "16px" }}>
                        Block Transition
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <input
                          type="number"
                          min={0}
                          max={30}
                          value={blockTransition || 0}
                          onChange={(e) => handleBlockTransitionChange(e.target.value)}
                          style={{
                            width: "80px",
                            padding: "6px 10px",
                            background: "#2c2c45",
                            border: "1px solid #004aad",
                            borderRadius: "6px",
                            color: "white"
                          }}
                        />
                        <span style={{ color: "#ccc" }}>seconds</span>
                        <button
                          onClick={() => handleBlockTransitionChange(0)}
                          style={{
                            background: "#e74c3c",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            cursor: "pointer"
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                   
                      </div>
                    
                      {/* ========== ADD TIME SUMMARY HERE ========== */}
                      <div style={{
                        marginBottom: "15px",
                        padding: "10px",
                        background: "rgba(255, 255, 255, 0.03)",
                        borderRadius: "6px",
                        border: "1px solid #004aad"
                      }}>
                        <div style={{ color: "#fcdc08", fontWeight: "bold", marginBottom: "5px" }}>
                          Time Summary:
                        </div>
                        <div style={{ color: "#ccc", fontSize: "14px", fontFamily: 'monospace' }}>
                          <div>Total Working: {formatDuration(currentSegments.reduce((sum, seg) => sum + (seg.workingTime || 0), 0))}</div>
                          <div>Total Rest: {formatDuration(currentSegments.reduce((sum, seg) => sum + (seg.restTime || 0), 0))}</div>
                          <div style={{ color: "#fcdc08", fontWeight: "bold" }}>Block Duration: {formatDuration(currentSegments.reduce((sum, seg) => sum + ((seg.workingTime || 0) + (seg.restTime || 0)), 0))}</div>
                        </div>
                      </div>
                      {/* ========== END TIME SUMMARY ========== */}
                    
                      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                        <button
                          onClick={() => setShowSegmentModal(false)}
                          style={{
                            padding: "8px 16px",
                            background: "#666",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer"
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={saveSegments}
                          style={{
                            padding: "8px 16px",
                            background: "#004aad",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer"
                          }}
                        >
                          Save Segments
                        </button>
                      </div>
                    </div>
                  </div>
                )}
          </div>
          <div className="notes-section">
            <h3 className="notes-title">Notes</h3>
           <div className="notes-title_textarea">
             <textarea
              value={notes}
              onChange={handleNotesChange}
              onBlur={() => {
                if (notes !== block.cooldown) {
                  debounceSave(exercises, blockDuration, timeSegments, notes);
                }
              }}
              style={{
                width: "100%",
                background: "rgb(0 0 0 / 0%)",
                border: "1px solid #444",
                color: "#fff",
                padding: 16,
                borderRadius: 12,
                minHeight: 100,
                fontSize: 15,
              }}
              placeholder="Type coaching cues..."
            />
           </div>
          </div>
        </div>
      </div>
    );
  };
  // Media Card
  // Extract filename from URL
  const getFilename = (url) => {
    if (!url || typeof url !== 'string') return '';
    try {
      const path = new URL(url).pathname;
      const name = path.split('/').pop();
      return decodeURIComponent(name || '');
    } catch { return url.split('/').pop() || ''; }
  };

  // MediaCard component with delete, video preview, filename display
  const MediaCard = ({ category, title, icon: Icon, accept, compact }) => {
    const url = media[category];
    const isUploading = uploading[category];
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const isVideo = accept === "video/*";
    const isAudio = accept === "audio/*";
    const isImage = accept === "image/*";
    const filename = getFilename(url);
    const truncatedName = filename.length > 28 ? filename.substring(0, 25) + '...' : filename;

    const handleFileClick = () => {
      if (!isUploading && !isDeleting) fileInputs[category].current?.click();
    };

    const handleDeleteMedia = async () => {
      if (!url) return;
      setIsDeleting(true);
      const token = localStorage.getItem("accessToken");
      try {
        const response = await fetch(`${apiPath.baseURL}${apiPath.removeMedia}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ media_url: url, category, day: activeDay })
        });
        const data = await response.json();
        if (data.status === 200) {
          message.success(`${title} deleted successfully!`);
          setFullWeekData(prev => {
            const newData = JSON.parse(JSON.stringify(prev));
            const wd = getWritableDayData(newData.full_week, activeDay);
            if (wd?.media) delete wd.media[category];
            return newData;
          });
        } else {
          message.error(data.message || "Failed to delete media");
        }
      } catch (error) {
        message.error("Error deleting media");
      } finally {
        setIsDeleting(false);
        setShowDeleteConfirm(false);
      }
    };

    // Compact audio bar layout
    if (compact && isAudio) {
      return (
        <div className="media-card">
          <input ref={fileInputs[category]} type="file" accept={accept} hidden onChange={(e) => handleFileSelect(e, category)} />
          <div style={{ fontSize: 11, fontWeight: "700", color: "#fcdc08", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, paddingLeft: 2 }}>{title}</div>
          <div style={{
            display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
            background: "rgba(26, 26, 46, 0.6)", border: url ? "2px solid #004aad" : "2px dashed #444",
            borderRadius: 8, cursor: isUploading || isDeleting ? "not-allowed" : "pointer",
            transition: "all 0.3s ease", minHeight: 52
          }}>
            {isUploading ? (
              <><div style={{ width: 24, height: 24, border: "2px solid #333", borderTop: "2px solid #004aad", borderRadius: "50%", animation: "spin 1s linear infinite", flexShrink: 0 }} /><span style={{ color: "#004aad", fontSize: 13, fontWeight: "bold" }}>Uploading...</span></>
            ) : isDeleting ? (
              <><div style={{ width: 24, height: 24, border: "2px solid #333", borderTop: "2px solid #e74c3c", borderRadius: "50%", animation: "spin 1s linear infinite", flexShrink: 0 }} /><span style={{ color: "#e74c3c", fontSize: 13, fontWeight: "bold" }}>Deleting...</span></>
            ) : url ? (
              <>
                <Music size={20} color="#004aad" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#888", fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{truncatedName}</div>
                  <audio controls preload="metadata" style={{ width: "100%", height: 28, marginTop: 4 }} src={url} />
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={(e) => { e.stopPropagation(); handleFileClick(); }} style={{ background: "#333", color: "#ccc", border: "none", borderRadius: 4, padding: "4px 8px", cursor: "pointer", fontSize: 11 }} title="Replace">Replace</button>
                  <button onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); }} style={{ background: "rgba(231,76,60,0.9)", color: "white", border: "none", borderRadius: 4, padding: "4px 8px", cursor: "pointer", fontSize: 11 }} title="Delete"><Trash2 size={12} /></button>
                </div>
              </>
            ) : (
              <div onClick={handleFileClick} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
                <Music size={20} color="#666" /><span style={{ color: "#888", fontSize: 13 }}>Click to upload {title.toLowerCase()}</span>
              </div>
            )}
          </div>
          {showDeleteConfirm && <DeleteConfirmModal title={title} category={category} onCancel={() => setShowDeleteConfirm(false)} onDelete={handleDeleteMedia} />}
        </div>
      );
    }

    // Standard card layout (videos, images)
    return (
      <div className="media-card">
        <input ref={fileInputs[category]} type="file" accept={accept} hidden onChange={(e) => handleFileSelect(e, category)} />
        <div style={{
          textAlign: "center", background: isUploading || isDeleting ? "rgba(26, 26, 46, 0.6)" : "rgba(26, 26, 46, 0.6)",
          border: url ? "2px solid #004aad" : "2px dashed #444", borderRadius: 8,
          height: isVideo ? 180 : 160, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
          cursor: isUploading || isDeleting ? "not-allowed" : "pointer", position: "relative", overflow: "hidden", transition: "all 0.3s ease"
        }}>
          {url && !isUploading && !isDeleting && (
            <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 6, zIndex: 10 }}>
              <button onClick={(e) => { e.stopPropagation(); handleFileClick(); }} style={{ background: "rgba(0,0,0,0.7)", color: "white", border: "none", borderRadius: 4, padding: "4px 8px", cursor: "pointer", fontSize: 11 }}>Replace</button>
              <button onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); }} style={{ background: "rgba(231,76,60,0.9)", color: "white", border: "none", borderRadius: "50%", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} title={`Delete ${title}`}><Trash2 size={14} /></button>
            </div>
          )}
          {isUploading ? (
            <><div style={{ width: 36, height: 36, border: "3px solid #333", borderTop: "3px solid #004aad", borderRadius: "50%", animation: "spin 1s linear infinite" }} /><div style={{ color: "#004aad", fontWeight: "bold", fontSize: 13, marginTop: 8 }}>Uploading...</div></>
          ) : isDeleting ? (
            <><div style={{ width: 36, height: 36, border: "3px solid #333", borderTop: "3px solid #e74c3c", borderRadius: "50%", animation: "spin 1s linear infinite" }} /><div style={{ color: "#e74c3c", fontWeight: "bold", fontSize: 13, marginTop: 8 }}>Deleting...</div></>
          ) : url ? (
            <>
              {isImage ? (
                <img src={url} alt={title} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6 }} onClick={handleFileClick} />
              ) : isVideo ? (
                <video controls preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6, background: "#000" }} src={url} />
              ) : (
                <div onClick={handleFileClick} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", background: "linear-gradient(135deg, #004aad 0%, #003d8f 100%)", borderRadius: 6, width: "100%" }}>
                  <CheckCircle size={36} color="#fff" /><div style={{ color: "#fff", fontSize: 13, marginTop: 8, fontWeight: "bold" }}>{title}</div>
                </div>
              )}
            </>
          ) : (
            <div onClick={handleFileClick} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <Icon size={40} color="#666" /><div style={{ fontWeight: "bold", fontSize: 13, color: "#ccc" }}>{title}</div><div style={{ color: "#666", fontSize: 11 }}>Click to upload</div>
            </div>
          )}
        </div>
        {url && truncatedName && <div style={{ color: "#888", fontSize: 11, marginTop: 4, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", padding: "0 4px" }} title={filename}>{truncatedName}</div>}
        {showDeleteConfirm && <DeleteConfirmModal title={title} category={category} onCancel={() => setShowDeleteConfirm(false)} onDelete={handleDeleteMedia} />}
      </div>
    );
  };

  // Shared delete confirmation modal
  const DeleteConfirmModal = ({ title, category, onCancel, onDelete }) => (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10002 }}>
      <div style={{ background: "rgba(26, 26, 46, 0.6)", padding: "30px", borderRadius: "12px", border: "2px solid #e74c3c", minWidth: "300px", textAlign: "center", boxShadow: "0 10px 40px rgba(0,0,0,0.5)" }}>
        <h3 style={{ color: "#e74c3c", marginBottom: "15px" }}>Delete {title}</h3>
        <p style={{ color: "#ccc", marginBottom: "20px" }}>Are you sure you want to delete this {category.includes('video') ? 'video' : category.includes('audio') ? 'audio' : 'image'}?</p>
        <p style={{ color: "#999", fontSize: "12px", marginBottom: "25px" }}>This action cannot be undone.</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={(e) => { e.stopPropagation(); onCancel(); }} style={{ padding: "8px 16px", background: "#666", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: "bold" }}>Cancel</button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} style={{ padding: "8px 16px", background: "#e74c3c", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: "bold" }}>Delete</button>
        </div>
      </div>
    </div>
  );
  if (loading) return <div className="loading-screen"><div className="spinner"></div><p>Loading...</p></div>;
  if (error) return <div className="error-screen"><p>{error}</p><button onClick={fetchWeeklyPlan}>Retry</button></div>;
  const ColorPicker = ({ label, value, onApply }) => {
  const [color, setColor] = useState(value);
  const debounceRef = useRef(null);

  // Update when parent value changes
  useEffect(() => {
    setColor(value);
  }, [value]);

  const handleChange = (e) => {
    const newColor = e.target.value;
    setColor(newColor);
    // Debounce: apply after 300ms of no changes (avoids hammering API while dragging)
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onApply(newColor);
    }, 300);
  };

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "10px",
      padding: "15px",
      background: "rgba(255, 255, 255, 0.03)",
      borderRadius: "10px",
      minWidth: "150px"
    }}>
      <div style={{ color: "#ccc", fontWeight: "600", fontSize: "14px" }}>{label}</div>

      <div style={{
        width: "70px",
        height: "70px",
        borderRadius: "10px",
        border: "3px solid #444",
        overflow: "hidden",
        cursor: "pointer",
        boxShadow: "0 4px 8px rgba(0,0,0,0.3)"
      }}>
        <input
          type="color"
          value={color}
          onChange={handleChange}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            cursor: "pointer",
            transform: "scale(1.5)"
          }}
          title={`Select ${label.toLowerCase()}`}
        />
      </div>

      <div style={{
        fontSize: "12px",
        color: "#888",
        fontFamily: "monospace",
        background: "rgba(0, 0, 0, 0.3)",
        padding: "4px 8px",
        borderRadius: "4px",
        minWidth: "100px",
        textAlign: "center"
      }}>
        {color}
      </div>
    </div>
  );
};
  if (!currentDay) return <div>No workout for {activeDay}</div>;

  const blocks = [];
  // Get all blocks dynamically (A_BLOCK, B_BLOCK, C_BLOCK, D_BLOCK, etc.)
  Object.keys(currentDay.data || {}).forEach(key => {
    if (key.endsWith("_BLOCK")) {
      blocks.push({ ...currentDay.data[key], title: key.replace("_", " "), blockKey: key });
    }
  });
  // Sort blocks alphabetically (A_BLOCK, B_BLOCK, C_BLOCK, D_BLOCK...)
  blocks.sort((a, b) => a.blockKey.localeCompare(b.blockKey));
  return (
    <div className="workout-container" style={{ position: "relative" }}>
      {/* Hidden Audio Elements */}
      {/* Excel modal removed */}
      
      {media.pre_hype_audio && (
        <audio
          ref={audioRefs.pre_hype}
          src={media.pre_hype_audio}
          loop
          onEnded={() => { setAudioPlaying(prev => ({ ...prev, pre_hype: false })); diagLog("audio_ended", { type: "pre_hype" }); }}
          onPause={() => { setAudioPlaying(prev => ({ ...prev, pre_hype: false })); diagLog("audio_pause", { type: "pre_hype" }); }}
          onPlay={() => setAudioPlaying(prev => ({ ...prev, pre_hype: true }))}
          onStalled={() => handleAudioStall("pre_hype", "stalled")}
          onWaiting={() => diagLog("audio_waiting", { type: "pre_hype" })}
          onError={() => diagLog("audio_error", { type: "pre_hype" })}
        />
      )}
      {media.warmup_audio && (
        <audio
          ref={audioRefs.warmup}
          src={media.warmup_audio}
          loop
          onEnded={() => { setAudioPlaying(prev => ({ ...prev, warmup: false })); diagLog("audio_ended", { type: "warmup" }); }}
          onPause={() => { setAudioPlaying(prev => ({ ...prev, warmup: false })); diagLog("audio_pause", { type: "warmup" }); }}
          onPlay={() => setAudioPlaying(prev => ({ ...prev, warmup: true }))}
          onStalled={() => handleAudioStall("warmup", "stalled")}
          onWaiting={() => diagLog("audio_waiting", { type: "warmup" })}
          onError={() => diagLog("audio_error", { type: "warmup" })}
        />
      )}
      {media.workout_audio && (
        <audio
          ref={audioRefs.workout}
          src={media.workout_audio}
          loop
          onEnded={() => { setAudioPlaying(prev => ({ ...prev, workout: false })); diagLog("audio_ended", { type: "workout" }); }}
          onPause={() => { setAudioPlaying(prev => ({ ...prev, workout: false })); diagLog("audio_pause", { type: "workout" }); }}
          onPlay={() => setAudioPlaying(prev => ({ ...prev, workout: true }))}
          onStalled={() => handleAudioStall("workout", "stalled")}
          onWaiting={() => diagLog("audio_waiting", { type: "workout" })}
          onError={() => diagLog("audio_error", { type: "workout" })}
        />
      )}
      {media.cooldown_audio && (
        <audio
          ref={audioRefs.cooldown}
          src={media.cooldown_audio}
          loop
          onEnded={() => { setAudioPlaying(prev => ({ ...prev, cooldown: false })); diagLog("audio_ended", { type: "cooldown" }); }}
          onPause={() => { setAudioPlaying(prev => ({ ...prev, cooldown: false })); diagLog("audio_pause", { type: "cooldown" }); }}
          onPlay={() => setAudioPlaying(prev => ({ ...prev, cooldown: true }))}
          onStalled={() => handleAudioStall("cooldown", "stalled")}
          onWaiting={() => diagLog("audio_waiting", { type: "cooldown" })}
          onError={() => diagLog("audio_error", { type: "cooldown" })}
        />
      )}
      <Container>
        <div className="workout-inner">
          <StableHeader __impl={SessionHeader} />
          
          {blocks.map((block, i) => (
            <StableBlock __impl={WorkoutBlock} key={`${activeDay}_${block.blockKey}`} block={block} blockKey={block.blockKey} sendWsCommand={sendWsCommand} />
          ))}
        
          {/* Add New Block Button */}
          <div style={{
            textAlign: "center",
            margin: "30px 0",
            padding: "20px",
            border: "2px dashed #004aad",
            borderRadius: "12px",
          }}>
            <button
              onClick={openAddBlockModal}
              style={{
                background: "#004aad",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: "0 auto",
                color: "#fcdc08" ,
              }}
            >
              <Plus size={20} />
              Add New Block
            </button>
          </div>
                {/* Excel buttons removed — use /programme page for bulk entry */}
          <div style={{ margin: "40px 0", paddingBottom: "80px" }}>
            <h2 style={{ color: "rgb(168, 168, 168)", marginBottom: 24, fontSize: 28, textAlign: "center" }}>
              Media for {activeDay}
            </h2>
            <Row gutter={[20, 20]}>
              {/* Pre-Hype: Video + Audio */}
              <Col xs={24} md={12} lg={8}>
                <h4 className="for-video-title">Pre-Hype Video</h4>
                <StableMedia __impl={MediaCard} category="pre_hype_video" title="Pre-Hype Video" icon={PlayCircle} accept="video/*" />
                <div style={{ marginTop: 10 }}>
                  <StableMedia __impl={MediaCard} category="pre_hype_audio" title="Pre-Hype Audio" icon={Music} accept="audio/*" compact />
                </div>
              </Col>
              {/* Warmup: Video + Audio */}
              <Col xs={24} md={12} lg={8}>
                <h4 className="for-video-title">Warm-Up Video</h4>
                <StableMedia __impl={MediaCard} category="warmup_video" title="Warm-Up Video" icon={PlayCircle} accept="video/*" />
                <div style={{ marginTop: 10 }}>
                  <StableMedia __impl={MediaCard} category="warmup_audio" title="Warm-Up Audio" icon={Music} accept="audio/*" compact />
                </div>
              </Col>
              {/* Workout Audio + Background Image */}
              <Col xs={24} md={12} lg={8}>
                <h4 className="for-video-title">Background Image</h4>
                <StableMedia __impl={MediaCard} category="background_image" title="Background Image" icon={Image} accept="image/*" />
                <div style={{ marginTop: 10 }}>
                  <StableMedia __impl={MediaCard} category="workout_audio" title="Workout Audio" icon={Music} accept="audio/*" compact />
                </div>
              </Col>
              {/* Cool-Down: Audio only */}
              <Col xs={24} md={12} lg={8}>
                <h4 className="for-video-title">Cool-Down Audio</h4>
                <StableMedia __impl={MediaCard} category="cooldown_audio" title="Cool-Down Audio" icon={Music} accept="audio/*" />
              </Col>
            </Row>
          </div>
          {/* Settings moved to side panel */}
        </div>
      </Container>
      {/* Settings Side Panel */}
      {showSettings && (
        <>
          {/* Backdrop */}
          <div onClick={() => setShowSettings(false)} style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 9998,
          }} />
          {/* Panel */}
          <div style={{
            position: "fixed", top: 0, right: 0, bottom: 0, width: "420px", maxWidth: "90vw",
            background: "#0d0d1a", borderLeft: "1px solid rgba(0,74,173,0.2)",
            boxShadow: "-10px 0 40px rgba(0,0,0,0.5)", zIndex: 9999,
            overflowY: "auto", padding: "24px",
          }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h2 style={{ color: "#fcdc08", fontSize: 22, fontWeight: 800, margin: 0 }}>{"\u2699"} Workout Settings</h2>
              <button onClick={() => setShowSettings(false)} style={{
                background: "rgba(255,255,255,0.06)", border: "none", color: "#fff",
                width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 18,
              }}>{"\u00d7"}</button>
            </div>

            {/* Fonts Section */}
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ color: "#fcdc08", fontSize: 14, fontWeight: 700, marginBottom: 12, letterSpacing: "0.05em" }}>FONTS</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ color: "#888", fontSize: 12, display: "block", marginBottom: 4 }}>Font A (Headings)</label>
                  <select value={fontA} onChange={(e) => setFontA(e.target.value)}
                    style={{ width: "100%", padding: "8px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 13 }}>
                    {["Horizon", "Microgramma", "Jupiter Brush", "Druk Wide Trial Bold", "Druk Wide Trial Heavy", "Inter", "MicroGBE"].map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                    <input type="color" value={fontAColor} onChange={(e) => setFontAColor(e.target.value)}
                      style={{ width: 32, height: 24, border: "none", cursor: "pointer", borderRadius: 4 }} />
                    <span style={{ color: "#888", fontSize: 11, fontFamily: "monospace" }}>{fontAColor}</span>
                  </div>
                </div>
                <div>
                  <label style={{ color: "#888", fontSize: 12, display: "block", marginBottom: 4 }}>Font B (Body)</label>
                  <select value={fontB} onChange={(e) => setFontB(e.target.value)}
                    style={{ width: "100%", padding: "8px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 13 }}>
                    {["Horizon", "Microgramma", "Jupiter Brush", "Druk Wide Trial Bold", "Druk Wide Trial Heavy", "Inter", "MicroGBE"].map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                    <input type="color" value={fontBColor} onChange={(e) => setFontBColor(e.target.value)}
                      style={{ width: 32, height: 24, border: "none", cursor: "pointer", borderRadius: 4 }} />
                    <span style={{ color: "#888", fontSize: 11, fontFamily: "monospace" }}>{fontBColor}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Colours Section */}
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ color: "#fcdc08", fontSize: 14, fontWeight: 700, marginBottom: 12, letterSpacing: "0.05em" }}>COLOURS</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div style={{ textAlign: "center" }}>
                  <label style={{ color: "#888", fontSize: 11, display: "block", marginBottom: 4 }}>Accent</label>
                  <input type="color" value={textColor} onChange={(e) => { setTextColor(e.target.value); setTempTextColor(e.target.value); }}
                    style={{ width: 48, height: 48, border: "2px solid #333", borderRadius: 8, cursor: "pointer" }} />
                  <div style={{ color: "#888", fontSize: 10, fontFamily: "monospace", marginTop: 4 }}>{textColor}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <label style={{ color: "#888", fontSize: 11, display: "block", marginBottom: 4 }}>Background</label>
                  <input type="color" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)}
                    style={{ width: 48, height: 48, border: "2px solid #333", borderRadius: 8, cursor: "pointer" }} />
                  <div style={{ color: "#888", fontSize: 10, fontFamily: "monospace", marginTop: 4 }}>{backgroundColor}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <label style={{ color: "#888", fontSize: 11, display: "block", marginBottom: 4 }}>Theme</label>
                  <input type="color" value={selectedColor} onChange={(e) => { setSelectedColor(e.target.value); setTempColor(e.target.value); }}
                    style={{ width: 48, height: 48, border: "2px solid #333", borderRadius: 8, cursor: "pointer" }} />
                  <div style={{ color: "#888", fontSize: 10, fontFamily: "monospace", marginTop: 4 }}>{selectedColor}</div>
                </div>
              </div>
            </div>

            {/* Messages Section */}
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ color: "#fcdc08", fontSize: 14, fontWeight: 700, marginBottom: 12, letterSpacing: "0.05em" }}>MESSAGES</h4>

              <label style={{ color: "#888", fontSize: 12, display: "block", marginBottom: 4 }}>Top Completion Message</label>
              <input value={topCompletionMessage} onChange={(e) => setTopCompletionMessage(e.target.value)} placeholder="Enter top completion message..."
                style={{ width: "100%", padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 14, marginBottom: 12 }} />

              <label style={{ color: "#888", fontSize: 12, display: "block", marginBottom: 4 }}>Completion Message</label>
              <input value={completionMessage} onChange={(e) => setCompletionMessage(e.target.value)} placeholder="Enter completion message..."
                style={{ width: "100%", padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 14, marginBottom: 12 }} />

              <label style={{ color: "#888", fontSize: 12, display: "block", marginBottom: 4 }}>Workout Transition Message</label>
              <input value={transitionMessage} onChange={(e) => setTransitionMessage(e.target.value)} placeholder="Enter transition message..."
                style={{ width: "100%", padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 14, marginBottom: 12 }} />
            </div>

            {/* Alert Chimes */}
            <div style={{ marginBottom: 28 }}>
              <h4 style={{ color: "#fcdc08", fontSize: 14, fontWeight: 700, marginBottom: 6, letterSpacing: "0.05em" }}>ALERT CHIMES</h4>
              <div style={{ color: "#888", fontSize: 12, marginBottom: 14 }}>Plays through your device when blocks are about to start or finish.</div>
              {["classic", "bell", "whistle", "off"].map((type) => {
                const selected = chimeType === type;
                return (
                  <div key={type} onClick={() => { setChimeType(type); try { localStorage.setItem("fh_chime_type", type); } catch {} }}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderRadius: 10, marginBottom: 8, cursor: "pointer", border: "1px solid", transition: "all 0.15s",
                      background: selected ? "rgba(252,220,8,0.08)" : "rgba(255,255,255,0.03)",
                      borderColor: selected ? "rgba(252,220,8,0.4)" : "rgba(255,255,255,0.08)",
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${selected ? "#fcdc08" : "#555"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {selected && <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#fcdc08" }} />}
                      </div>
                      <span style={{ color: selected ? "#fff" : "#aaa", fontWeight: 600, fontSize: 14, textTransform: "capitalize" }}>{type === "off" ? "Off" : type.charAt(0).toUpperCase() + type.slice(1)}</span>
                    </div>
                    {type !== "off" && (
                      <div style={{ display: "flex", gap: 6 }}>
                        {["start", "end", "done"].map((moment) => (
                          <button key={moment} onClick={(e) => { e.stopPropagation(); playBell(moment === "done" ? "complete" : "transition"); }}
                            style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.06)", color: "#ccc", fontSize: 11, fontWeight: 600, cursor: "pointer", textTransform: "capitalize" }}>
                            {moment.charAt(0).toUpperCase() + moment.slice(1)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Coach View PIN */}
            <div style={{ marginBottom: 28 }}>
              <h4 style={{ color: "#fcdc08", fontSize: 14, fontWeight: 700, marginBottom: 6, letterSpacing: "0.05em" }}>COACH VIEW PIN</h4>
              <div style={{ color: "#888", fontSize: 12, marginBottom: 14 }}>Share this PIN with coaches so they can view the weekly programme without logging in.</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <div style={{ flex: 1, padding: "16px 20px", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, textAlign: "center" }}>
                  <span style={{ color: "#fff", fontSize: 32, fontWeight: 800, letterSpacing: "0.22em", fontFamily: "monospace" }}>{coachPin}</span>
                </div>
                <button onClick={() => {
                  const pin = String(Math.floor(100000 + Math.random() * 900000));
                  setCoachPin(pin);
                  try { localStorage.setItem("fh_coach_pin", pin); } catch {}
                }} style={{ padding: "14px 18px", background: "linear-gradient(135deg,#fcdc08,#e6c800)", border: "none", borderRadius: 10, color: "#0d0d1a", fontWeight: 800, fontSize: 13, cursor: "pointer" }}>
                  Regenerate
                </button>
              </div>
              <div style={{ color: "#666", fontSize: 11 }}>Coaches enter this PIN at gym.fusionhub.studio to view the programme</div>
            </div>

            {/* Save Button */}
            <button onClick={() => {
              autoSave({
                new_text_color: textColor,
                new_background_color: backgroundColor,
                new_color: selectedColor,
                new_font_a: fontA,
                new_font_a_color: fontAColor,
                new_font_b: fontB,
                new_font_b_color: fontBColor,
                new_completion_message: completionMessage,
                new_top_completion_message: topCompletionMessage,
                new_transition_message: transitionMessage,
              });
              if (socket && socket.readyState === WebSocket.OPEN) {
                try {
                  socket.send(JSON.stringify({ type: "update_color", day: activeDay, color: selectedColor }));
                  socket.send(JSON.stringify({ type: "update_text_color", day: activeDay, text_color: textColor }));
                  socket.send(JSON.stringify({ type: "update_background_color", day: activeDay, background_color: backgroundColor }));
                  socket.send(JSON.stringify({ type: "update_font_a", day: activeDay, font_a: fontA, font_a_color: fontAColor }));
                  socket.send(JSON.stringify({ type: "update_font_b", day: activeDay, font_b: fontB, font_b_color: fontBColor }));
                } catch (err) {}
              }
              setShowSettings(false);
            }} style={{
              width: "100%", padding: "14px", background: "linear-gradient(135deg, #004aad 0%, #003d8f 100%)",
              border: "none", borderRadius: 10, color: "#fff", fontSize: 15, fontWeight: 700,
              cursor: "pointer", letterSpacing: "0.03em",
              boxShadow: "0 4px 14px rgba(0,74,173,0.3)",
            }}>
              Save & Apply Settings
            </button>
          </div>
        </>
      )}

      {/* Saved Indicator */}
      <div style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        width: 16,
        height: 16,
        background: "#004aad",
        borderRadius: "50%",
        opacity: justSaved ? 1 : 0,
        transition: "opacity 0.4s",
        boxShadow: "0 0 20px #004aad",
        zIndex: 9999
      }} />
    
      {/* Add Block Modal */}
      {showAddBlockModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000
        }}>
          <div style={{
            background: "rgba(26, 26, 46, 0.6)",
            padding: "40px",
            borderRadius: "16px",
            border: "3px solid #004aad",
            minWidth: "400px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)"
          }}>
            <h2 style={{
              color: "#fcdc08",
              marginBottom: "30px",
              textAlign: "center",
              fontSize: "24px",
              fontWeight: "bold"
            }}>Add New Block</h2>
          
            <div style={{ marginBottom: "20px" }}>
              <label style={{
                color: "#fff",
                display: "block",
                marginBottom: "8px",
                fontWeight: "bold"
              }}>Block Name:</label>
              <Input
                value={newBlockName}
                onChange={(e) => setNewBlockName(e.target.value.toUpperCase())}
                placeholder="D_BLOCK"
                style={{
                  background: "rgba(255, 255, 255, 0.03)",
                  border: "2px solid #004aad",
                  color: "#fff",
                  fontSize: "16px",
                  padding: "12px",
                  borderRadius: "8px"
                }}
              />
            </div>
          
            <div style={{ marginBottom: "30px" }}>
              <label style={{
                color: "#fff",
                display: "block",
                marginBottom: "8px",
                fontWeight: "bold"
              }}>Block Title:</label>
              <Input
                value={newBlockTitle}
                onChange={(e) => setNewBlockTitle(e.target.value)}
                placeholder="Cardio Block"
                style={{
                  background: "rgba(255, 255, 255, 0.03)",
                  border: "2px solid #004aad",
                  color: "#fff",
                  fontSize: "16px",
                  padding: "12px",
                  borderRadius: "8px"
                }}
              />
            </div>
          
            <div style={{
              display: "flex",
              gap: "15px",
              justifyContent: "center"
            }}>
              <button
                onClick={() => {
                  setShowAddBlockModal(false);
                  setNewBlockName("");
                  setNewBlockTitle("");
                }}
                style={{
                  padding: "12px 24px",
                  background: "#666",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold"
                }}
              >
                Cancel
              </button>
              <button
                onClick={addNewBlock}
                style={{
                  padding: "12px 24px",
                  background: "#004aad",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                  boxShadow: "0 4px 15px rgba(76, 175, 80, 0.4)"
                }}
              >
                Add Block
              </button>
            </div>
          </div>
        </div>
      )}
    
      {/* Delete Block Modal */}
      {showDeleteModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000
        }}>
          <div style={{
            background: "rgba(26, 26, 46, 0.6)",
            padding: "40px",
            borderRadius: "16px",
            border: "3px solid #e74c3c",
            minWidth: "400px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            textAlign: "center"
          }}>
            <h2 style={{
              color: "#e74c3c",
              marginBottom: "20px",
              fontSize: "24px",
              fontWeight: "bold"
            }}>Delete Block</h2>
          
            <p style={{
              color: "#fff",
              fontSize: "16px",
              marginBottom: "30px"
            }}>Are you sure you want to delete <strong>{blockToDelete}</strong>?</p>
          
            <p style={{
              color: "#ccc",
              fontSize: "14px",
              marginBottom: "30px"
            }}>This action cannot be undone.</p>
          
            <div style={{
              display: "flex",
              gap: "15px",
              justifyContent: "center"
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setBlockToDelete("");
                }}
                style={{
                  padding: "12px 24px",
                  background: "#666",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold"
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteBlock}
                style={{
                  padding: "12px 24px",
                  background: "#e74c3c",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                  boxShadow: "0 4px 15px rgba(231, 76, 60, 0.4)"
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  
};
export default Dashboard;